import { handoffRequestKey, completeHandoffRequest } from "@/lib/handoff-request";
import { availabilityNotice, type LiveAvailability } from "@/lib/live-chat-availability";
import { effectivePreChatFields, type PreChatField } from "@/lib/prechat-fields";
import { getAuthToken } from "@/lib/queryClient";
import { LIVE_RECOVERY_KEY, LIVE_BROWSER_KEY, recoveryScope, readLiveRecovery, writeLiveRecovery, restoreLiveMessages, tokenIdentity, type RecoveryScope, type LiveRecovery } from "@/lib/visitor-live-recovery";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useRoute, useLocation, Redirect } from "wouter";
import ReactMarkdown from "react-markdown";
import { KnowledgeCitations, type KnowledgeCitation } from "@/components/knowledge-citations";
import remarkGfm from "remark-gfm";
import { Bot, Send, Plus, MessageSquare, Menu, X, Trash2, User, Sparkles, ArrowLeft, ArrowDown, Headphones, Paperclip, Mic, BookOpen, Settings, HelpCircle, Sun, Moon, Languages, Maximize2 } from "lucide-react";
import { CHAT_LANGUAGES, AUTO_LANGUAGE_HELP } from "@/lib/chat-languages";
import { getMessageLangMeta, prefersReducedMotion } from "@/lib/a11y";
import { advanceFullscreenAnnouncer, initialFullscreenAnnouncerState, shouldRenderAnnouncer, type FullscreenAnnouncerState } from "@/lib/a11y-announcer";
import { readableTextOn } from "@/lib/themes";
import { isNearBottom, scrollAnchorIntoView } from "@/lib/scroll";
import { useStaffTheme } from "@/hooks/use-staff-theme";
import { hydrateChatState, persistChatState } from "./fullscreen-chat.hydration";
import {
  isVerifiedStaffPortalEntry,
  shouldRedirectUnreadyStaffPortal,
} from "./fullscreen-chat.entry";
import {
  archiveConversation,
  bulkArchiveConversations,
  restoreConversations,
} from "./fullscreen-chat.archive";
import { diagSetFlags, diagRecordFocus, diagRecordAnnouncement } from "@/lib/a11y-diagnostics";

interface Message {
  citations?: KnowledgeCitation[];
  id: string;
  // "agent"  → live human operator messages (rendered with a distinct
  //            badge so users can tell the AI bot from a person).
  // "system" → status updates ("Connecting…", "Operator joined", etc).
  role: "user" | "assistant" | "agent" | "system";
  content: string;
  timestamp: Date;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentMime?: string;
}

// The capability is retained only in identity-bound tab storage.
// Transcripts and current status are always recovered from the server.
type LiveState = {
  sessionId: string;
  retryScope?: string;
  // Per-session signed capability returned by /visitor/start (or
  // /handoff). Required on every subsequent visitor REST call and
  // websocket subscription so a leaked session GUID alone is useless.
  visitorToken: string;
  status: "queued" | "active" | "escalated" | "closed";
  position: number | null;
  operatorName: string | null;
  // Track the count of messages we've already merged from the server
  // so polling only appends new ones.
  knownMessageIds: Set<string>;
};

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  sessionId: string;
  backendConversationId: string | null;
  createdAt: Date;
}

interface PredefinedQuestion {
  question: string;
  category?: string;
  sortOrder?: number;
}

interface BotConfig {
  name: string;
  primaryColor: string;
  welcomeMessage: string;
  botIcon: string;
  theme: string;
  displayMode?: string;
  // "authenticated" → end-user must be signed in (default for staff bots).
  // "public" → legacy behaviour, anyone with apiKey may chat.
  accessMode?: "authenticated" | "public";
  // When true, anonymous visitors must complete a Name/Email/Phone contact
  // form (with a data-collection consent notice) before they can chat.
  preChatForm?: boolean;
  preChatFields?: PreChatField[];
  predefinedQuestions?: PredefinedQuestion[];
  // Org-level ADA/WCAG compliance toggle: when true, chat produces
  // screen-reader status announcements (typing + completed replies).
  a11yAnnouncements?: boolean;
  staffPortalReadiness?: import("@/lib/staff-portal-readiness").StaffPortalReadiness;
}

// Curated list of dialling codes for the pre-chat phone field. Kept short
// and ordered by likely use rather than exhaustive.
const COUNTRY_CODES: { label: string; code: string }[] = [
  { label: "US/Canada (+1)", code: "+1" },
  { label: "United Kingdom (+44)", code: "+44" },
  { label: "India (+91)", code: "+91" },
  { label: "Australia (+61)", code: "+61" },
  { label: "Germany (+49)", code: "+49" },
  { label: "France (+33)", code: "+33" },
  { label: "Spain (+34)", code: "+34" },
  { label: "Italy (+39)", code: "+39" },
  { label: "Brazil (+55)", code: "+55" },
  { label: "Mexico (+52)", code: "+52" },
  { label: "Nigeria (+234)", code: "+234" },
  { label: "South Africa (+27)", code: "+27" },
  { label: "UAE (+971)", code: "+971" },
  { label: "Singapore (+65)", code: "+65" },
  { label: "Japan (+81)", code: "+81" },
  { label: "China (+86)", code: "+86" },
];

interface AuthMe {
  user: { id: string; email: string; name: string | null; role: string | null; orgName: string | null } | null;
}

function formatRelativeTime(d: Date): string {
  const now = Date.now();
  const t = d.getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString();
}

function generateSessionId() {
  return "fs-" + Math.random().toString(36).substring(2, 15);
}

// Render markdown links (e.g. the bot's admission-form download links) as
// real anchors that open in a new tab so a click downloads/opens the PDF
// without navigating away from the chat.
const markdownComponents = {
  a: ({ node, children, ...props }: any) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-700 break-words"
    >
      {children}
    </a>
  ),
};

// Inline preview for attachments — images render directly, everything
// else falls back to a download link with the original filename.
function renderAttachment(msg: { attachmentUrl?: string; attachmentName?: string; attachmentMime?: string }) {
  if (!msg.attachmentUrl) return null;
  if (msg.attachmentMime?.startsWith("image/")) {
    return (
      <img
        src={msg.attachmentUrl}
        alt={msg.attachmentName ?? "attachment"}
        className="mt-2 max-w-full max-h-72 rounded-lg border border-slate-200"
      />
    );
  }
  return (
    <a
      href={msg.attachmentUrl}
      download={msg.attachmentName ?? "file"}
      className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs"
    >
      <Paperclip className="w-3.5 h-3.5" />
      {msg.attachmentName ?? "Download file"}
    </a>
  );
}

export default function FullscreenChat() {
  // This page backs two routes: /chat/:apiKey (the full-screen STAFF
  // platform) and /widget/:apiKey (the compact PUBLIC widget window).
  // The variant is derived from whichever route matched.
  const [, chatParams] = useRoute("/chat/:apiKey");
  const [matchWidget, widgetParams] = useRoute("/widget/:apiKey");
  const apiKey = chatParams?.apiKey || widgetParams?.apiKey || "";
  const isWidget = !!matchWidget;
  const [, setLocation] = useLocation();
  // Staff test portal sets ?role=<name> so the bot tailors its answers to that
  // role. Passed through to /api/v1/chat as `as_role` (a prompt persona hint).
  const portalRole = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("role")
    : null;
  // The portal's "I'm staff" card lands here with ?from=portal. The staff
  // surface only recognises a signed-in user (their JWT is forwarded as
  // X-User-Token so the backend runs identity-bound tools like
  // create_jira_issue under their identity). If an anonymous visitor reaches
  // this staff entry — via the gateway race or a bookmarked deep link — we
  // must send them to sign in first rather than let them chat with no tools.
  const fromPortal = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("from") === "portal"
    : false;
  // The staff portal's "I'm a guest" path opens /widget/:apiKey?guest=1. In
  // guest mode we ignore the platform session entirely (even if a signed-in
  // admin launched it): the visitor is treated as a true anonymous guest, so
  // the pre-chat contact form shows and the staff token is never forwarded.
  const guestMode = isWidget && (typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).has("guest")
    : false);
  const [botConfig, setBotConfig] = useState<BotConfig | null>(null);
  const preChatFields = effectivePreChatFields(botConfig?.preChatFields);
  const pcField = (key: string) => preChatFields.find(f => f.key === key)!;
  const visitorHeaders = () => ({ "Content-Type": "application/json",
    ...(!guestMode && getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) });
  const [authedUser, setAuthedUser] = useState<AuthMe["user"] | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authBotKey, setAuthBotKey] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // The most recently archived conversation(s), kept so the visitor can Undo
  // (POST /api/v1/my/conversations/{id}/restore) before navigating away. Holds
  // the full local objects so a successful restore re-inserts them verbatim.
  const [recentlyArchived, setRecentlyArchived] = useState<Conversation[]>([]);
  // Inline error shown near the Recent Chat section when an archive or
  // restore request fails on the server (task #79: never show false success).
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [activeConvoId, setActiveConvoId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Initialized from the SAME media query as isNarrow so a narrow first
  // paint never flashes the drawer open before effects run.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !(typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches),
  );
  // Narrow layout (≈300–400% zoom or a phone-sized viewport): the sidebar
  // becomes an overlay drawer instead of a static column, so the header,
  // thread, and composer always keep the full width. matchMedia picks up
  // browser-zoom changes live (zoom shrinks the CSS viewport width).
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  // Short viewport (real browser zoom shrinks the CSS viewport HEIGHT too:
  // 400% zoom on a laptop leaves ~180–220px). In compact mode the header and
  // composer drop to icon-only / minimal padding so the message history
  // always keeps meaningful visible height.
  const [isShort, setIsShort] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-height: 500px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-height: 500px)");
    const onChange = () => setIsShort(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Org-level ADA/WCAG Compliance toggle (surfaced as a11yAnnouncements in
  // the public bot config; backend derives it from the ada_wcag framework).
  // PRODUCT REQUIREMENT: the high-zoom accessibility UI (compact header/
  // composer, guaranteed history height, follow-latest + "New reply" chip,
  // announcements) activates ONLY when this toggle is ON. With it OFF the
  // chat keeps the standard responsive UI, regardless of viewport geometry.
  const adaWcagEnabled = botConfig?.a11yAnnouncements === true;
  // WCAG 1.4.10 Reflow: compact layout activates for ANY narrow/short viewport
  // (real browser zoom shrinks the CSS viewport — 400% on 1280px ≈ 320px wide).
  // The ADA toggle gates extra screen-reader ANNOUNCEMENTS only; the structural
  // reflow must be unconditional so the UI is usable at high zoom without
  // enabling any additional setting.
  const compact = isNarrow || isShort;
  const adaWcag = adaWcagEnabled; // announcements only — toggle-gated
  // Ref mirror so stable callbacks (focusComposer targets) can read the
  // current toggle without being re-created when it changes.
  const adaWcagRef = useRef(false);
  adaWcagRef.current = adaWcagEnabled;
  // Runtime a11y diagnostics flags (native implementation only — the
  // third-party accessiBe overlay has been removed).
  useEffect(() => {
    if (!botConfig) return;
    diagSetFlags({
      adaWcagEnabled,
      a11yAnnouncements: !!botConfig.a11yAnnouncements,
      botOwnerOrganizationId: (botConfig as any).organizationId ?? null,
    });
  }, [adaWcagEnabled, botConfig]);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => {
      setIsNarrow(mq.matches);
      // Entering narrow layout: start with the drawer closed so it never
      // covers the thread; leaving it: restore the static column.
      setSidebarOpen(!mq.matches);
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Narrow drawer = real modal (it declares aria-modal): on open, focus
  // moves to its first control; Tab is trapped inside; Escape closes and
  // restores focus to the menu button. Selecting a conversation / New chat
  // closes it and hands focus to the composer instead (by design).
  const drawerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isNarrow || !sidebarOpen) return;
    const drawer = drawerRef.current;
    const focusables = () =>
      Array.from(
        drawer?.querySelectorAll<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])') ?? [],
      ).filter((el) => el.offsetParent !== null);
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSidebarOpen(false);
        menuBtnRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        const els = focusables();
        if (els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (!drawer?.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isNarrow, sidebarOpen]);
  const [configError, setConfigError] = useState(false);
  const [live, setLive] = useState<LiveState | null>(null);
  const [recoveringLive, setRecoveringLive] = useState(true);
  const recoveryScopeRef = useRef<RecoveryScope | null>(null);
  const recoveryGeneration = useRef(0);
  // Mirror of `live` for reading the latest committed value inside timers
  // (the queued no-agent timeout) without re-subscribing them on every change.
  const liveRef = useRef<LiveState | null>(null);
  liveRef.current = live;
  const [handoffPending, setHandoffPending] = useState(false);
  // Departments available for routing a live-agent handoff. Only populated
  // for signed-in users (the list endpoint is org-scoped + authed); anonymous
  // visitors get an empty list and skip straight to the general queue.
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [deptsLoaded, setDeptsLoaded] = useState(false);
  // Set when the visitor clicks "Talk to a person" before the department
  // list has resolved — we defer the handoff until the fetch settles so a
  // signed-in user never skips the picker due to a slow list request.
  const [pendingTalk, setPendingTalk] = useState(false);
  const [showDeptPicker, setShowDeptPicker] = useState(false);
  const [handoffDept, setHandoffDept] = useState<string>("");
  // When the visitor clicks "Talk to a person" but no operators are
  // online, we surface a small inline form so they can leave their
  // name + email + message instead of getting stuck in an empty queue.
  const [offlineForm, setOfflineForm] = useState<null | { name: string; email: string; message: string; submitted: boolean; submissionKey: string; notice: string; consent: boolean }>(null);
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineError, setOfflineError] = useState("");
  const offlineStorageKey = () => "offline-draft:" + JSON.stringify(recoveryScopeRef.current);
  useEffect(() => {
    if (!offlineForm || offlineForm.submitted) return;
    try { sessionStorage.setItem(offlineStorageKey(), JSON.stringify(offlineForm)); }
    catch { setOfflineError("Your draft could not be saved in this browser. Please enable browser storage before submitting."); }
  }, [offlineForm]);
  const showOfflineCapture = (a: LiveAvailability) => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(offlineStorageKey()) || "null"); } catch {}
    const draft = saved?.submissionKey ? { ...saved, notice: availabilityNotice(a) }
      : { name: authedUser?.name ?? contact?.name ?? "", email: authedUser?.email ?? contact?.email ?? "", message: "", submitted: false,
          submissionKey: crypto.randomUUID(), notice: availabilityNotice(a), consent: false };
    try { sessionStorage.setItem(offlineStorageKey(), JSON.stringify(draft)); } catch {}
    setOfflineForm(previous => previous ?? draft);
    setOfflineError("");
  };
  // ── Pre-chat contact form (anonymous visitors only) ──────────────
  // Captured contact is stored server-side as a lead bound to this
  // session id, and threaded into the chat as user_name/user_email so
  // the conversation gets attributed.
  const [contact, setContact] = useState<{ name: string; email: string; countryCode: string; phone: string } | null>(null);
  const [preChatDone, setPreChatDone] = useState(false);
  // Set when the backend demands a consented lead (PRECHAT_REQUIRED) even though
  // the client thought the visitor was exempt — e.g. a user signed into a
  // DIFFERENT org opening this org's public bot. They aren't staff for THIS bot,
  // so we fall back to the public pre-chat contact form.
  const [forcePreChat, setForcePreChat] = useState(false);
  const resumeHandoffAfterPrechat = useRef<{ departmentId: string | null } | null>(null);
  const preChatDraft = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(`dotnetbot.prechat.draft.${apiKey}`) || "null"); }
    catch { return null; }
  }, [apiKey]);
  const [pcName, setPcName] = useState(preChatDraft?.name ?? "");
  const [pcEmail, setPcEmail] = useState(preChatDraft?.email ?? "");
  const [pcCountry, setPcCountry] = useState(preChatDraft?.countryCode ?? "+1");
  const [pcPhone, setPcPhone] = useState(preChatDraft?.phone ?? "");
  const [pcConsent, setPcConsent] = useState(preChatDraft?.consent === true);
  const [preChatBot, setPreChatBot] = useState(apiKey);
  useEffect(() => {
    setPcName(preChatDraft?.name ?? ""); setPcEmail(preChatDraft?.email ?? "");
    setPcCountry(preChatDraft?.countryCode ?? "+1"); setPcPhone(preChatDraft?.phone ?? "");
    setPcConsent(preChatDraft?.consent === true);
    setPreChatDone(false); setForcePreChat(false); setContact(null); setPreChatBot(apiKey);
    resumeHandoffAfterPrechat.current = null;
  }, [apiKey, preChatDraft]);
  const [pcError, setPcError] = useState<string | null>(null);
  // Which pre-chat field failed validation ("name" | "email" | "phone" |
  // "consent") — drives aria-invalid + aria-describedby on that field.
  const [pcErrorField, setPcErrorField] = useState<string | null>(null);
  const [pcSubmitting, setPcSubmitting] = useState(false);
  // A stable session id minted up-front so the captured lead and the
  // first conversation share the same session id.
  const gateSessionId = useMemo(() => preChatDraft?.sessionId || generateSessionId(), [preChatDraft]);
  const requireUpdatedPreChat = async (message: string) => {
    const scope = recoveryScopeRef.current;
    if (!scope || scope.apiKey !== apiKey || readLiveRecovery(sessionStorage, scope)?.sessionId) return;
    try {
      const response = await fetch(`/api/v1/chatbots/${apiKey}/config`, { cache: "no-store" });
      const config = response.ok ? await response.json() : null;
      if (scope !== recoveryScopeRef.current) return;
      if (config?.name) setBotConfig(config);
    } catch {}
    if (scope !== recoveryScopeRef.current) return;
    const prior = readLiveRecovery(sessionStorage, scope);
    resumeHandoffAfterPrechat.current = { departmentId: typeof prior?.request.departmentId === "string" ? prior.request.departmentId : null };
    sessionStorage.removeItem(LIVE_RECOVERY_KEY);
    sessionStorage.removeItem(`dotnetbot.prechat.${apiKey}`);
    setPreChatDone(false); setForcePreChat(true); setPcError(message);
  };
  useEffect(() => {
    if (!apiKey || preChatDone || preChatBot !== apiKey) return;
    try { sessionStorage.setItem(`dotnetbot.prechat.draft.${apiKey}`, JSON.stringify({
      sessionId: gateSessionId, name: pcName, email: pcEmail, countryCode: pcCountry, phone: pcPhone, consent: pcConsent
    })); } catch {}
  }, [apiKey, gateSessionId, pcName, pcEmail, pcCountry, pcPhone, pcConsent, preChatDone, preChatBot]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Focus the composer on the next frame (after drawer transitions /
  // conversation state changes render), using the stable textarea ref —
  // the input element is never remounted or keyed.
  const focusComposer = useCallback(() => {
    diagRecordFocus("fullscreen-chat composer");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);
  const recognitionRef = useRef<any>(null);
  const inputBeforeVoiceRef = useRef<string>("");
  // True only when the user has clicked Voice and we haven't been
  // asked to stop. We use this to auto-restart recognition when
  // Chrome silently ends the session after a pause (continuous=true
  // is *not* enough on Chrome — the engine still stops after silence).
  const wantRecordingRef = useRef<boolean>(false);
  // Highest result index already folded into the per-instance buffer.
  // Browsers (Chrome especially) re-emit earlier results across
  // onresult events; we skip anything at/below this to avoid duplicate
  // text. Reset on every fresh recognition instance.
  const committedFinalIndexRef = useRef<number>(-1);
  // Per-instance transcript collected while a single SR session is
  // running. On instance end we fold this into transcriptSegmentsRef
  // (with overlap dedupe across restarts) and clear it.
  const instanceTranscriptRef = useRef<string>("");
  // All committed transcript chunks from prior instances in this
  // recording session. Joined and flushed into the input on finalize.
  const transcriptSegmentsRef = useRef<string[]>([]);
  // Session id increments each time the user starts a recording. Every
  // SR instance captures its own sid; stale callbacks (late onresult
  // / onend after stop, or after a new session began) ignore work.
  const recordingSessionIdRef = useRef<number>(0);
  // True from the moment the user clicks Stop until the SR instance's
  // onend has fired and we've flushed. Prevents flushing twice and
  // suppresses auto-restart.
  const isStoppingRef = useRef<boolean>(false);
  // Highest session id that has already been flushed. Hard-prevents a
  // second finalize for the same recording session (e.g. when the
  // safety-net timer fires moments before a delayed onend).
  const finalizedSidRef = useRef<number>(-1);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    try { return localStorage.getItem("dotnetbot.chatLanguage") || ""; } catch { return ""; }
  });
  // Guided menu (public compact widget only): which predefined-question
  // category the visitor is currently browsing. null = show the top-level
  // "area of inquiry" picker.
  const [menuCategory, setMenuCategory] = useState<string | null>(null);

  // Web Speech API (Chrome/Edge/Safari) — feature-detect once.
  useEffect(() => {
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    setVoiceSupported(!!SR);
  }, []);

  // Merge a new chunk into the cumulative segment list, removing
  // any word-level overlap with the tail of the previous segment.
  // Chrome sometimes repeats the last spoken phrase at the start of
  // the next instance after auto-restart, which is the main source of
  // user-visible "lots of repetition". We strip it here.
  const appendSegmentWithDedupe = useCallback((chunk: string) => {
    const norm = chunk.trim();
    if (!norm) return;
    const segs = transcriptSegmentsRef.current;
    const prev = segs[segs.length - 1];
    if (!prev) {
      segs.push(norm);
      return;
    }
    if (prev === norm || prev.endsWith(norm)) return;
    const prevWords = prev.split(/\s+/);
    const nextWords = norm.split(/\s+/);
    // Try the longest possible overlap (up to 12 words) where the
    // suffix of prev equals the prefix of next, case-insensitive,
    // ignoring trailing punctuation.
    const max = Math.min(12, prevWords.length, nextWords.length);
    let overlap = 0;
    const stripPunct = (s: string) => s.replace(/[.,!?;:"'`]/g, "").toLowerCase();
    for (let n = max; n > 0; n--) {
      let match = true;
      for (let i = 0; i < n; i++) {
        if (stripPunct(prevWords[prevWords.length - n + i]) !== stripPunct(nextWords[i])) {
          match = false;
          break;
        }
      }
      if (match) {
        overlap = n;
        break;
      }
    }
    const trimmed = nextWords.slice(overlap).join(" ").trim();
    if (trimmed) segs.push(trimmed);
  }, []);

  // Flush the accumulated transcript into the textarea exactly once
  // per recording session. Subsequent calls for the same session id
  // (e.g. delayed onend after the safety-net fired) are ignored, and
  // the session id is bumped so any straggling callbacks from the
  // closed instance can never resurrect state.
  const finalizeRecording = useCallback(() => {
    const sid = recordingSessionIdRef.current;
    if (finalizedSidRef.current >= sid) {
      // Already finalized — just make sure UI is clean.
      isStoppingRef.current = false;
      setIsRecording(false);
      return;
    }
    finalizedSidRef.current = sid;
    if (instanceTranscriptRef.current.trim()) {
      appendSegmentWithDedupe(instanceTranscriptRef.current);
    }
    instanceTranscriptRef.current = "";
    const spoken = transcriptSegmentsRef.current.join(" ").trim();
    transcriptSegmentsRef.current = [];
    isStoppingRef.current = false;
    wantRecordingRef.current = false;
    // Bump the session id so any late onresult/onend/onerror from the
    // (now-closed) SR instance fail their `sid !== current` guard.
    recordingSessionIdRef.current += 1;
    recognitionRef.current = null;
    setIsRecording(false);
    if (spoken) {
      const base = inputBeforeVoiceRef.current;
      const joiner = base && !base.endsWith(" ") ? " " : "";
      const next = (base + joiner + spoken).slice(0, 1500);
      setInput(next);
      // Programmatic focus move is ADA-toggle gated (see focus policy).
      if (adaWcagRef.current) inputRef.current?.focus();
    }
  }, [appendSegmentWithDedupe]);

  // Build a recognition instance and start it. Each call creates a
  // fresh SR — never reuse, since Chrome's same-instance restart
  // preserves the prior results list and reintroduces duplication.
  const startRecognition = useCallback(() => {
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = false; // we don't render live, so save the work
    recognition.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    const sid = recordingSessionIdRef.current;

    recognition.onresult = (event: any) => {
      // Ignore results from a stale instance (user already stopped or
      // a new session began). This is the P0 fix for late-event leaks.
      if (sid !== recordingSessionIdRef.current) return;
      let added = "";
      for (let i = committedFinalIndexRef.current + 1; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          added += r[0].transcript;
          committedFinalIndexRef.current = i;
        }
      }
      if (added) {
        instanceTranscriptRef.current += added;
      }
    };
    recognition.onerror = (e: any) => {
      if (sid !== recordingSessionIdRef.current) return;
      // Engine timeouts on silence are benign — onend will handle
      // restart-or-finalize. Same for our own stop() (aborted).
      const benign = e?.error === "no-speech" || e?.error === "aborted";
      if (benign) return;
      const msg = e?.error === "not-allowed"
        ? "Microphone permission was denied. Please allow mic access in your browser."
        : e?.error === "audio-capture"
        ? "No microphone was found. Please check your device."
        : `Voice input error: ${e?.error || "unknown"}`;
      setVoiceError(msg);
      // Preserve everything successfully transcribed up to this point
      // (transcriptSegmentsRef + instanceTranscriptRef) — finalize
      // will fold and flush them so the user doesn't lose dictation
      // when an error hits mid-session. We only force the engine
      // closed and route through finalize so the UI can never get
      // stuck on "Recording…" if onend is swallowed.
      wantRecordingRef.current = false;
      isStoppingRef.current = true;
      try { recognition.stop(); } catch {}
      finalizeRecording();
    };
    recognition.onend = () => {
      // Stale instance — just drop it.
      if (sid !== recordingSessionIdRef.current) {
        if (recognitionRef.current === recognition) recognitionRef.current = null;
        return;
      }
      // Fold this instance's transcript into the cumulative segments
      // BEFORE deciding whether to restart, so we never lose audio
      // captured between Chrome's silence cut-off and the auto-restart.
      if (instanceTranscriptRef.current.trim()) {
        appendSegmentWithDedupe(instanceTranscriptRef.current);
        instanceTranscriptRef.current = "";
      }
      recognitionRef.current = null;
      // User is still recording AND tab is visible AND not stopping ⇒
      // start a fresh instance after a tiny delay (lets the audio
      // device fully release).
      const tabHidden = typeof document !== "undefined" && document.hidden;
      if (wantRecordingRef.current && !isStoppingRef.current && !tabHidden) {
        setTimeout(() => {
          // Re-check; user may have stopped during the delay.
          if (sid === recordingSessionIdRef.current && wantRecordingRef.current && !isStoppingRef.current) {
            startRecognition();
          }
        }, 120);
        return;
      }
      // Terminal end — flush exactly once.
      finalizeRecording();
    };

    try {
      committedFinalIndexRef.current = -1;
      instanceTranscriptRef.current = "";
      recognition.start();
      recognitionRef.current = recognition;
    } catch {
      // recognition.start() failed — common on auto-restart after a
      // silence cutoff (Chrome may briefly hold the mic). If we have
      // any speech buffered from prior instances in this session,
      // flush it via finalizeRecording so the user never loses
      // dictated text just because the next instance couldn't start.
      const hasBuffered =
        transcriptSegmentsRef.current.length > 0 ||
        instanceTranscriptRef.current.trim().length > 0;
      if (hasBuffered) {
        setVoiceError("Mic interrupted — inserted what was captured so far.");
        wantRecordingRef.current = false;
        isStoppingRef.current = true;
        recognitionRef.current = null;
        finalizeRecording();
      } else {
        setVoiceError("Couldn't start voice input. Please try again.");
        wantRecordingRef.current = false;
        isStoppingRef.current = false;
        setIsRecording(false);
        recognitionRef.current = null;
      }
    }
  }, [appendSegmentWithDedupe, finalizeRecording]);

  // Single stop path used by the mic click, the visibility / pagehide
  // listeners, and any other "we need to stop now" trigger. Always
  // schedules a finalize safety net so the UI never gets stuck on
  // "Recording…" if the browser swallows onend (Safari edge cases).
  const requestStopAndFinalize = useCallback(() => {
    if (!isRecording && !wantRecordingRef.current && !isStoppingRef.current) return;
    wantRecordingRef.current = false;
    isStoppingRef.current = true;
    try {
      recognitionRef.current?.stop();
    } catch {
      // No active engine — finalize directly.
      finalizeRecording();
      return;
    }
    const sid = recordingSessionIdRef.current;
    setTimeout(() => {
      if (isStoppingRef.current && sid === recordingSessionIdRef.current) {
        finalizeRecording();
      }
    }, 750);
  }, [finalizeRecording, isRecording]);

  const toggleVoiceRecording = useCallback(() => {
    setVoiceError(null);
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setVoiceError("Voice input isn't supported in this browser. Try Chrome, Edge, or Safari.");
      return;
    }
    if (wantRecordingRef.current) {
      // User asked to stop. Don't flush yet — just signal intent and
      // let the active SR's onend run finalizeRecording exactly once
      // (with a safety-net timeout in case onend never fires).
      requestStopAndFinalize();
      return;
    }
    // Reject taps that arrive during the stop/finalize window (the
    // user already asked to stop and we're waiting on onend / the
    // safety-net timer). Without this guard a fast second tap would
    // increment the session id and clear transcriptSegmentsRef before
    // the prior session's flush had a chance to run.
    if (isStoppingRef.current || isRecording) return;
    // Start a new recording session.
    recordingSessionIdRef.current += 1;
    inputBeforeVoiceRef.current = input;
    transcriptSegmentsRef.current = [];
    instanceTranscriptRef.current = "";
    isStoppingRef.current = false;
    wantRecordingRef.current = true;
    setIsRecording(true);
    startRecognition();
  }, [input, isRecording, startRecognition, requestStopAndFinalize]);

  // Stop recording when the tab is hidden — Chrome will throttle the
  // mic anyway and any partial audio risks being lost or duplicated
  // on resume.
  useEffect(() => {
    const onHide = () => {
      if (wantRecordingRef.current) {
        // Route through the same single stop path the mic click uses
        // — guarantees finalize fires (with safety net) and the UI
        // resets to "not recording" rather than getting stuck.
        requestStopAndFinalize();
      }
    };
    const onVisibility = () => {
      if (document.hidden) onHide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
    };
  }, [requestStopAndFinalize]);

  // Stop recording on unmount.
  useEffect(() => () => {
    wantRecordingRef.current = false;
    isStoppingRef.current = true;
    recordingSessionIdRef.current += 1; // invalidate any pending callbacks
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeConvo = conversations.find((c) => c.id === activeConvoId) || null;

  // Load bot config and the current platform session in parallel. We need
  // both before deciding whether to show the chat or a "please sign in" gate.
  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    Promise.all([
      fetch(`/api/v1/chatbots/${apiKey}/config`, { cache: "no-store" }).then((r) => {
        if (r.status >= 500) throw new Error("Configuration temporarily unavailable");
        return r.ok ? r.json() : null;
      }).catch(() => ({
        name: "Chat", primaryColor: "#0c3547", welcomeMessage: "How can we help?",
        botIcon: "", theme: "professional-blue", preChatForm: true, accessMode: "public"
      })),
      fetch("/api/auth/me", {
        credentials: "include",
        headers: ((): Record<string, string> => {
          try {
            const t = getAuthToken();
            return t ? { Authorization: `Bearer ${t}` } : {};
          } catch { return {}; }
        })(),
      }).then((r) => (r.ok ? r.json() : { user: null })).catch(() => ({ user: null })),
    ]).then(([cfg, me]) => {
      if (cancelled) return;
      if (cfg && cfg.name) {
        setBotConfig(cfg as BotConfig);
      } else {
        setConfigError(true);
      }
      // /api/auth/me returns a FLAT object ({ id, email, fullName, role,
      // organizationName, … }), not { user: {…} }. Map it into the signed-in
      // principal we render so staff see their identity + forward their token.
      setAuthedUser((() => {
        if (guestMode) return null;
        const m = me as any;
        if (!m || !m.id) return null;
        return {
          id: String(m.id),
          email: m.email ?? "",
          name: m.fullName ?? m.userName ?? null,
          role: m.role ?? null,
          orgName: m.organizationName ?? null,
        };
      })());
      // If this visitor already completed the pre-chat form earlier in
      // the same browser session, don't prompt again.
      try {
        if (sessionStorage.getItem(`dotnetbot.prechat.${apiKey}`) === "1") {
          setPreChatDone(true);
          const saved = JSON.parse(sessionStorage.getItem(`dotnetbot.prechat.contact.${apiKey}`) || "null");
          if (saved?.contact && saved?.sessionId) setContact(saved.contact);
        }
      } catch {/* sessionStorage unavailable — fall through to prompt */}
      setAuthChecked(true);
      setAuthBotKey(apiKey);
      // Best-effort: signed-in users can route a handoff to a department.
      // Anonymous visitors get 401 here and silently fall back to the queue.
      fetch("/api/departments/list", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => { if (!cancelled && Array.isArray(d)) setDepartments(d); })
        .catch(() => { /* anonymous — no departments */ })
        .finally(() => { if (!cancelled) setDeptsLoaded(true); });
    });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Authoritative staff-entry guard: once auth has resolved, a ?from=portal
  // visitor who is NOT signed in is bounced to login (returning here after)
  // so the staff chat is never entered anonymously. Only fires after
  // authChecked, so an already-signed-in user is never redirected.
  useEffect(() => {
    if (!authChecked || isWidget || !fromPortal || authedUser) return;
    setLocation(`/login?redirect=${encodeURIComponent(`/chat/${apiKey}?from=portal`)}`);
  }, [authChecked, authedUser, isWidget, fromPortal, apiKey, setLocation]);

  // Only an authenticated entry handed off by the Staff Portal is subject to
  // readiness redirects. A normal shared /chat/:apiKey link remains a public
  // chat surface and must retain its pre-chat flow even when the bot is not
  // ready for role-aware staff chat.
  useEffect(() => {
    if (!botConfig) return;
    if (shouldRedirectUnreadyStaffPortal(
      { isWidget, fromPortal, hasAuthenticatedUser: !!authedUser },
      botConfig.staffPortalReadiness,
    ))
      setLocation(`/portal/${apiKey}`);
  }, [botConfig, isWidget, fromPortal, authedUser, apiKey, setLocation]);

  // ── Auto-scroll (WCAG 1.4.10 / 400% zoom safe) ────────────────
  // Never scrollIntoView: at high zoom it also scrolls every scrollable
  // ancestor (the page), which pushed the header/composer out of the
  // viewport and hid the thread. Instead lib/scroll.ts detects the actual
  // scroll owner — <main> normally, the DOCUMENT at 300–400% zoom when the
  // page reflows taller than the viewport — and drives it directly so the
  // START of the latest message is visible (not the composer).
  const chatMainRef = useRef<HTMLElement>(null);
  // Separate anchors for the newest user message and newest bot/agent reply.
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  const latestBotMsgRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the reader was already near the bottom BEFORE the new
  // message rendered, so we never yank them away from history they scrolled
  // up to read. Updated on container AND window scroll (zoom reflow).
  const nearBottomRef = useRef(true);
  // Follow-latest mode: ON by default; turned OFF ONLY by an explicit user
  // scroll gesture (wheel / touch / scroll keys / scrollbar drag) that lands
  // away from the bottom. Programmatic scrolls, zoom reflow, ResizeObserver
  // churn, and layout changes fire scroll events too — those must NEVER
  // disable following or raise the "New reply" chip (false-chip bug at 400%).
  const followLatestRef = useRef(true);
  const userScrollIntentAtRef = useRef(0);
  // Auto-scroll intentionally suppressed while the reader is up in history —
  // show a "New reply" jump chip instead of force-scrolling them.
  const [newReplyPending, setNewReplyPending] = useState(false);
  const syncNearBottom = () => {
    const el = chatMainRef.current;
    if (!el) return;
    const near = isNearBottom(el);
    nearBottomRef.current = near;
    if (near) {
      // Reader is back at the latest message — resume following.
      followLatestRef.current = true;
      setNewReplyPending(false);
    } else if (Date.now() - userScrollIntentAtRef.current < 1500) {
      // Away from bottom AND a real user gesture just happened.
      followLatestRef.current = false;
    }
  };
  const handleChatScroll = syncNearBottom;
  useEffect(() => {
    // ADA/WCAG toggle OFF: the follow-latest/"New reply" machinery stays
    // fully inert — no listeners, no chip, standard scroll behavior only.
    if (!adaWcag) {
      followLatestRef.current = true;
      setNewReplyPending(false);
      return;
    }
    const markIntent = () => { userScrollIntentAtRef.current = Date.now(); };
    const isFormField = (t: EventTarget | null) =>
      t instanceof HTMLElement && !!t.closest("textarea, input, select, [contenteditable]");
    const markKeyIntent = (e: KeyboardEvent) => {
      // Typing in the composer (spaces, arrows while editing) is NOT scroll
      // intent — that's what falsely raised the "New reply" chip mid-typing.
      if (isFormField(e.target)) return;
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) markIntent();
    };
    const markPointerIntent = (e: PointerEvent | MouseEvent) => {
      // Only a press inside the history panel counts — clicking Send or
      // other controls must never disable follow-latest. NOTE: Chrome fires
      // mousedown (not pointerdown) for scrollbar drags, and the scrollbar's
      // target IS the scrolling element itself — so listen to both.
      const main = chatMainRef.current;
      if (main && e.target instanceof Node && main.contains(e.target)) markIntent();
    };
    // The locked app shell means the document itself never scrolls; <main>
    // is the only scroll owner, but we keep the window listener as a safety
    // net for any environment where the document briefly owns scrolling.
    window.addEventListener("scroll", syncNearBottom, { passive: true });
    window.addEventListener("wheel", markIntent, { passive: true });
    window.addEventListener("touchmove", markIntent, { passive: true });
    window.addEventListener("pointerdown", markPointerIntent, { passive: true });
    window.addEventListener("mousedown", markPointerIntent, { passive: true });
    window.addEventListener("keydown", markKeyIntent);
    return () => {
      window.removeEventListener("scroll", syncNearBottom);
      window.removeEventListener("wheel", markIntent);
      window.removeEventListener("touchmove", markIntent);
      window.removeEventListener("pointerdown", markPointerIntent);
      window.removeEventListener("mousedown", markPointerIntent);
      window.removeEventListener("keydown", markKeyIntent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adaWcag]);
  const scrollToLatest = (anchor: HTMLElement | null) => {
    const el = chatMainRef.current;
    if (!el) return;
    // WCAG 2.3.3: respect the user's reduced-motion preference.
    scrollAnchorIntoView(el, anchor, prefersReducedMotion());
    nearBottomRef.current = true;
    followLatestRef.current = true;
    setNewReplyPending(false);
  };
  useEffect(() => {
    if (!chatMainRef.current || messages.length === 0) return;
    const last = messages[messages.length - 1];
    const own = last.role === "user";
    // Sending a message always re-engages follow-latest; replies follow only
    // when the reader hasn't deliberately scrolled up into history.
    if (own) followLatestRef.current = true;
    // Chip only under the ADA/WCAG toggle; otherwise standard behavior
    // (always bring the latest message into view).
    if (!own && adaWcag && !followLatestRef.current) {
      setNewReplyPending(true);
      return;
    }
    const anchor = own
      ? latestUserMsgRef.current
      : last.role === "system"
        ? null
        : latestBotMsgRef.current;
    // rAF: let the new bubble finish layout (markdown/images) before measuring.
    requestAnimationFrame(() => scrollToLatest(anchor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, adaWcag]);
  // Anchor indices for the ref callbacks in the message map below.
  const lastUserIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "user") return i;
    return -1;
  })();
  const lastBotIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const r = messages[i].role;
      if (r === "assistant" || r === "agent") return i;
    }
    return -1;
  })();

  // ── Initial composer focus ────────────────────────────────────
  // Once the chat UI is actually rendered (config loaded, past any pre-chat
  // gate), put the caret in the composer — but never steal focus from a
  // control the user is already using.
  const didInitialFocusRef = useRef(false);
  useEffect(() => {
    // Accessibility-specific focus management: only when the org ADA/WCAG
    // toggle is ON. OFF = no automatic focus on page load.
    if (!adaWcagEnabled) return;
    if (didInitialFocusRef.current || !botConfig) return;
    if (!inputRef.current) return; // gated view (pre-chat / access) — not rendered yet
    const ae = document.activeElement;
    if (ae && ae !== document.body && ae !== document.documentElement) return;
    didInitialFocusRef.current = true;
    inputRef.current.focus();
  });

  // ── Screen-reader announcements (WCAG 4.1.3 Status Messages) ──
  // One polite live region announces (a) a concise typing/loading status and
  // (b) each completed assistant/agent reply exactly once. Streaming tokens
  // are never announced (replies arrive whole), and the region is replaced —
  // not appended — so the conversation is never re-read.
  const [srAnnouncement, setSrAnnouncement] = useState("");
  // Org ADA/WCAG toggle: app-generated announcements only run when enabled.
  // Structural accessibility (labels, keyboard, zoom, RTL) is always on.
  const announceEnabled = !!botConfig?.a11yAnnouncements;
  // Pure toggle-aware announcer (lib/a11y-announcer.ts, unit-tested):
  // ON → announce-once machine (baselines on convo switch/hydration);
  // OFF → pending narration cancelled, essential errors still announce once;
  // OFF→ON → re-baselines so old history is never re-read.
  const announcerRef = useRef<FullscreenAnnouncerState>(initialFullscreenAnnouncerState);
  useEffect(() => {
    const next = advanceFullscreenAnnouncer(announcerRef.current, {
      enabled: announceEnabled,
      convoId: activeConvoId,
      messages,
      isLoading,
    });
    announcerRef.current = next;
    setSrAnnouncement(next.announcement);
    if (announceEnabled) diagRecordAnnouncement(next.announcement, "fullscreen-chat announcer");
  }, [messages, isLoading, activeConvoId, announceEnabled]);

  // ── Conversation persistence ──────────────────────────────────
  // We keep every thread in localStorage, scoped per bot apiKey, so
  // AUTHENTICATED bots let signed-in users refresh the page (or come
  // back tomorrow) and still pick up any past conversation. The
  // sidebar's "Recent Chat" list is hydrated from this on mount.
  //
  // PUBLIC bots (accessMode !== "authenticated") always start fresh:
  // every visit/refresh opens a single new, empty conversation and we
  // never restore prior threads from the browser. Past conversations
  // are NOT lost — they remain fully available server-side in the
  // admin Conversations (chat logs) page. We also clear any previously
  // stored public history so stale threads never linger in the browser.
  const storageKey = apiKey ? `dotnetbot.chat.${apiKey}` : "";
  // Resolved only once botConfig has loaded. Public = anything that is
  // not explicitly "authenticated".
  const isPublicBot = !!botConfig && botConfig.accessMode !== "authenticated";
  // STAFF surface for a signed-in user: their threads live on the server
  // (tied to their account), not in this browser. In this mode we skip the
  // localStorage hydrate/persist path entirely and load/continue/delete via
  // the /api/v1/my/conversations endpoints instead.
  const serverMode = !!authedUser && !isWidget;
  const serverLoadedKeyRef = useRef<string | null>(null);
  const jwtToken = () => { try { return localStorage.getItem("dotnetbot.jwt"); } catch { return null; } };
  // Track which storageKey we've actually hydrated for. If the route's
  // apiKey changes mid-mount (SPA navigation between two bots), we
  // re-hydrate the new bot's history and reset in-memory state so we
  // never write the previous bot's threads into the new bot's slot.
  const hydratedKeyRef = useRef<string | null>(null);

  // Recover only a capability owned by this bot, identity, browser and tab.
  // No transcript is kept in browser storage; even closed sessions re-read
  // their authoritative transcript and status from the visitor endpoint.
  useEffect(() => {
    if (!apiKey || !authChecked || authBotKey !== apiKey || !botConfig) return;
    const generation = ++recoveryGeneration.current;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const initialIdentity = tokenIdentity(getAuthToken());
    const current = () => !cancelled && generation === recoveryGeneration.current &&
      tokenIdentity(getAuthToken()) === initialIdentity;
    setRecoveringLive(true);
    setLive(null);
    setOfflineForm(null); setOfflineError(""); setOfflineBusy(false);
    let scope: RecoveryScope;
    let record: LiveRecovery | null;
    try {
      scope = recoveryScope(apiKey, `${guestMode ? "guest-mode" : authedUser?.id ?? "guest"}`,
        localStorage, sessionStorage);
      recoveryScopeRef.current = scope;
      record = readLiveRecovery(sessionStorage, scope);
    } catch {
      recoveryScopeRef.current = null;
      setRecoveringLive(false);
      return;
    }
    const invalidate = () => {
      if (cancelled) return;
      ++recoveryGeneration.current;
      recoveryScopeRef.current = null;
      try { sessionStorage.removeItem(LIVE_RECOVERY_KEY); } catch {}
      setLive(null); setMessages([]); setConversations([]); setActiveConvoId(null);
      // Re-run authoritative authentication before showing any conversation.
      window.location.reload();
    };
    const checkIdentity = () => {
      if (tokenIdentity(getAuthToken()) !== initialIdentity ||
          localStorage.getItem(LIVE_BROWSER_KEY) !== scope.browser) invalidate();
    };
    const identityTimer = setInterval(checkIdentity, 500);
    window.addEventListener("storage", checkIdentity);
    window.addEventListener("focus", checkIdentity);
    window.addEventListener("visitor-identity-changed", invalidate);
    const recover = async () => {
      if (!record || !current()) { if (current()) setRecoveringLive(false); return; }
      try {
        if (!record.sessionId) {
          const res = await fetch("/api/live-chat/handoff", {
            method: "POST", credentials: "omit",
            headers: { "Content-Type": "application/json", "Idempotency-Key": record.requestKey },
            body: JSON.stringify({ ...record.request, recoverOnly: !!sessionStorage.getItem(offlineStorageKey()) }),
          });
          if (!current()) return;
          if (!res.ok) {
            const error = await res.json().catch(() => null);
            if (error?.code === "LIVE_CHAT_UNAVAILABLE") {
              showOfflineCapture(error.availability); setRecoveringLive(false); return;
            }
            if (error?.code === "PRECHAT_REQUIRED") {
              await requireUpdatedPreChat(error.message); setRecoveringLive(false); return;
            }
            throw new Error("Handoff recovery pending");
          }
          const session = await res.json();
          if (!current()) return;
          record = { ...record, sessionId: session.id, visitorToken: session.visitorToken };
          writeLiveRecovery(sessionStorage, record);
        }
        const res = await fetch(`/api/live-chat/visitor/${record.sessionId}/state`, {
          credentials: "omit",
          headers: { "X-Visitor-Token": record.visitorToken!, "X-Chatbot-Key": apiKey },
        });
        if (!current()) return;
        if ([401, 403, 404].includes(res.status)) {
          sessionStorage.removeItem(LIVE_RECOVERY_KEY);
          setRecoveringLive(false);
          return;
        }
        if (!res.ok) throw new Error("Recovery pending");
        const state = await res.json();
        if (!current()) return;
        const restored = restoreLiveMessages(state.messages);
        if (state.status === "closed") restored.push({
          id: `closed:${record.sessionId}`, role: "system", content: "This live chat has ended.",
          timestamp: new Date(), attachmentUrl: undefined, attachmentName: undefined, attachmentMime: undefined,
        });
        const convo: Conversation = {
          id: `live:${record.sessionId}`, title: "Live chat", messages: restored,
          sessionId: record.retryScope, backendConversationId: null, createdAt: new Date(),
        };
        setConversations(prev => [convo, ...prev.filter(c => c.id !== convo.id)]);
        setActiveConvoId(convo.id);
        setMessages(restored);
        setLive({ sessionId: record.sessionId!, visitorToken: record.visitorToken!,
          retryScope: record.retryScope, status: state.status, position: state.position,
          operatorName: state.operatorName, knownMessageIds: new Set(state.messages.map((m: { id: string }) => m.id)) });
        setRecoveringLive(false);
      } catch {
        // Keep the capability and retry the same session, never start a new one.
        if (current()) retry = setTimeout(recover, 3000);
      }
    };
    void recover();
    return () => {
      cancelled = true; clearTimeout(retry); clearInterval(identityTimer);
      window.removeEventListener("storage", checkIdentity);
      window.removeEventListener("focus", checkIdentity);
      window.removeEventListener("visitor-identity-changed", invalidate);
    };
  }, [apiKey, authBotKey, authChecked, authedUser?.id, guestMode, botConfig]);

  useEffect(() => {
    // Wait for botConfig so we know whether this bot is public or
    // authenticated before deciding to hydrate.
    if (!storageKey || !botConfig || hydratedKeyRef.current === storageKey) return;
    // Signed-in staff: the server is the source of truth — never touch
    // localStorage (a separate effect loads their account threads).
    if (serverMode) { hydratedKeyRef.current = storageKey; return; }
    // Switching bots mid-session — clear prior in-memory state before
    // hydration to avoid a render flash with the wrong conversations.
    if (hydratedKeyRef.current !== null) {
      setConversations([]);
      setActiveConvoId(null);
      setMessages([]);
    }
    // Public bots: helper clears stored history and returns empty state.
    // Authenticated bots: helper parses + revives the stored thread list.
    const { conversations: hydrated, active } = hydrateChatState(
      localStorage,
      storageKey,
      isPublicBot,
    );
    if (!isPublicBot && hydrated.length > 0) {
      setConversations(hydrated);
      if (active) {
        setActiveConvoId(active.id);
        setMessages(active.messages);
      }
    }
    // Mark hydrated AFTER state setters so the persist effect below
    // doesn't write the just-cleared empty state back into the slot.
    hydratedKeyRef.current = storageKey;
  }, [storageKey, botConfig, isPublicBot, serverMode]);

  useEffect(() => {
    // Only persist once we've finished hydrating *this* key, so we
    // never clobber a stored history with the transient empty state
    // that exists during a bot switch. Public bots never persist their
    // thread list (the helper short-circuits) so every visit starts
    // fresh — writing history back would re-create the stale-thread
    // problem on the next load.
    if (!storageKey || !botConfig || hydratedKeyRef.current !== storageKey) return;
    // Server-backed staff threads are never mirrored into localStorage.
    if (serverMode) return;
    persistChatState(localStorage, storageKey, isPublicBot, conversations, activeConvoId);
  }, [storageKey, conversations, activeConvoId, botConfig, isPublicBot, serverMode]);

  // ── Server-backed staff history ───────────────────────────────
  // Signed-in staff: load this user's own saved threads for the bot from
  // the server (ChatGPT-style). Messages are lazy-loaded when a thread is
  // opened (selectConversation), so the list stays light.
  useEffect(() => {
    if (!apiKey || !botConfig || !serverMode) return;
    if (serverLoadedKeyRef.current === storageKey) return;
    serverLoadedKeyRef.current = storageKey;
    const token = jwtToken();
    (async () => {
      try {
        const res = await fetch(`/api/v1/my/conversations?apiKey=${encodeURIComponent(apiKey)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const rows = (await res.json()) as Array<{
          id: string; sessionId: string; title: string;
          messageCount: number; createdAt: string; lastActivityAt: string;
        }>;
        const loaded: Conversation[] = rows.map((r) => ({
          id: r.id,
          title: r.title || "New conversation",
          messages: [],
          sessionId: r.sessionId,
          backendConversationId: r.id,
          createdAt: new Date(r.createdAt),
        }));
        setConversations(prev => [...prev.filter(c => c.id.startsWith("live:")), ...loaded]);
      } catch {
        // Network/auth failure — leave the sidebar empty rather than crash.
      }
    })();
  }, [apiKey, botConfig, serverMode, storageKey]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + "px";
    }
  }, [input]);

  const startNewConversation = useCallback(() => {
    if (liveRef.current && liveRef.current.status !== "closed") return;
    sessionStorage.removeItem(LIVE_RECOVERY_KEY);
    setLive(null);
    const newConvo: Conversation = {
      id: "c-" + Date.now(),
      title: "New conversation",
      messages: [],
      sessionId: generateSessionId(),
      backendConversationId: null,
      createdAt: new Date(),
    };
    setConversations((prev) => [newConvo, ...prev]);
    setActiveConvoId(newConvo.id);
    setMessages([]);
    followLatestRef.current = true;
    setNewReplyPending(false);
    // On the narrow layout the drawer covers the chat — close it, then put
    // the caret in the composer so typing can start immediately.
    setSidebarOpen((open) => (isNarrow ? false : open));
    // Programmatic focus after New Chat is accessibility-specific: only
    // when the org ADA/WCAG toggle is ON (ref keeps this callback stable).
    if (adaWcagRef.current) focusComposer();
  }, [isNarrow, focusComposer]);

  const submitPreChat = async () => {
    setPcError(null);
    setPcErrorField(null);
    const name = pcField("name").visible ? pcName.trim() : "";
    const email = pcField("email").visible ? pcEmail.trim() : "";
    const digits = pcField("phone").visible ? pcPhone.replace(/\D/g, "") : "";
    // On validation failure: persistent inline error (role=alert), the
    // offending field is marked aria-invalid + described by the error, and
    // focus moves to it. Entered values are preserved.
    const fail = (field: string, msg: string) => {
      setPcErrorField(field);
      setPcError(msg);
      requestAnimationFrame(() => document.getElementById(`pc-${field === "consent" ? "consent" : field}`)?.focus());
    };
    if (pcField("name").required && !name) { fail("name", "Please enter your name."); return; }
    if ((pcField("email").required || email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { fail("email", "Please enter a valid email address."); return; }
    if ((pcField("phone").required || digits) && (digits.length < 6 || digits.length > 15)) { fail("phone", "Please enter a valid phone number."); return; }
    if (!pcConsent) { fail("consent", "Please agree to the data-collection notice to continue."); return; }

    setPcSubmitting(true);
    try {
      const res = await fetch(`/api/v1/chatbots/${apiKey}/lead`, {
        method: "POST",
        headers: visitorHeaders(),
        body: JSON.stringify({
          sessionId: gateSessionId,
          name,
          email,
          countryCode: pcField("countryCode").visible ? pcCountry : "",
          phone: digits,
          consent: true,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        setPcError(d?.message || "Couldn't submit your details. Please try again.");
        return;
      }
      try { sessionStorage.setItem(`dotnetbot.prechat.${apiKey}`, "1"); } catch {/* ignore */}
      const saved = await res.json();
      const captured = saved.contact ?? { name, email, countryCode: pcCountry, phone: digits };
      setContact(captured);
      try { sessionStorage.setItem(`dotnetbot.prechat.contact.${apiKey}`, JSON.stringify({ sessionId: gateSessionId, contact: captured })); } catch {}
      // Seed the first conversation with the same session id used for the
      // lead so the captured contact and the conversation line up.
      const convo: Conversation = {
        id: "c-" + Date.now(),
        title: "New conversation",
        messages: [],
        sessionId: gateSessionId,
        backendConversationId: null,
        createdAt: new Date(),
      };
      setConversations([convo]);
      setActiveConvoId(convo.id);
      setMessages([]);
      setPreChatDone(true);
    } catch {
      setPcError("Couldn't submit your details. Please try again.");
    } finally {
      setPcSubmitting(false);
    }
  };

  const sendMessage = async (overrideText?: string) => {
    const messageText = (overrideText ?? input).trim();
    if (!messageText || isLoading) return;

    const userMsg: Message = {
      id: "m-" + Date.now(),
      role: "user",
      content: messageText,
      timestamp: new Date(),
    };

    setInput("");
    setIsLoading(true);
    // Clicking the Send button moved focus onto the button (which then
    // disables → focus falls to <body>). Returning the caret is an
    // accessibility-managed focus move, so it is ADA-toggle gated; with
    // the toggle OFF, Enter-key sends never blur the textarea anyway.
    if (adaWcagRef.current) focusComposer();

    let targetConvoId = activeConvoId;
    let targetSessionId: string;

    if (targetConvoId) {
      const convo = conversations.find((c) => c.id === targetConvoId);
      targetSessionId = convo?.sessionId || generateSessionId();

      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetConvoId
            ? {
                ...c,
                title: c.messages.length === 0 ? messageText.substring(0, 40) + (messageText.length > 40 ? "..." : "") : c.title,
                messages: [...c.messages, userMsg],
              }
            : c
        )
      );
    } else {
      const newConvoId = "c-" + Date.now();
      targetSessionId = generateSessionId();
      const newConvo: Conversation = {
        id: newConvoId,
        title: messageText.substring(0, 40) + (messageText.length > 40 ? "..." : ""),
        messages: [userMsg],
        sessionId: targetSessionId,
        backendConversationId: null,
        createdAt: new Date(),
      };
      setConversations((prev) => [newConvo, ...prev]);
      setActiveConvoId(newConvoId);
      targetConvoId = newConvoId;
    }

    setMessages((prev) => [...prev, userMsg]);

    try {
      // Signed-in staff forward their platform JWT so the backend recognises
      // them on this (bot-apiKey-authed) endpoint. Anonymous visitors send none.
      const staffToken = authedUser
        ? (() => { try { return localStorage.getItem("dotnetbot.jwt"); } catch { return null; } })()
        : null;
      const res = await fetch("/api/v1/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
          // The Authorization header is reserved for the bot apiKey, so a
          // signed-in staff member forwards their platform JWT separately. The
          // backend uses it to recognise staff (full-screen, role-aware, no
          // pre-chat form); anonymous visitors simply omit it.
          ...(staffToken ? { "X-User-Token": staffToken } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          message: messageText,
          session_id: targetSessionId,
          ...(contact ? { user_name: contact.name, user_email: contact.email } : {}),
          ...(portalRole ? { as_role: portalRole } : {}),
          ...(isVerifiedStaffPortalEntry({
            isWidget,
            fromPortal,
            hasAuthenticatedUser: !!authedUser,
          }) ? { staff_portal: true } : {}),
          ...(selectedLanguage ? { language: selectedLanguage } : {}),
        }),
      });

      // Readiness can change after this page loaded (for example, an admin
      // removes the last active KB source). The server is authoritative: drop
      // the optimistic message and return to the gateway, which re-fetches
      // config and shows the exact missing setup items.
      if (res.status === 409) {
        let code = "";
        try { code = (await res.clone().json())?.code ?? ""; } catch { /* non-JSON body */ }
        if (code === "STAFF_PORTAL_NOT_READY" && isVerifiedStaffPortalEntry({
          isWidget,
          fromPortal,
          hasAuthenticatedUser: !!authedUser,
        })) {
          setMessages((prev) => prev.filter((message) => message.id !== userMsg.id));
          setLocation(`/portal/${apiKey}`);
          return;
        }
      }

      // Handle the auth-required gate explicitly so the user gets a clear
      // path back to login instead of a generic "trouble connecting" toast.
      if (res.status === 401) {
        const errorMsg: Message = {
          id: "m-" + Date.now() + "-err",
          role: "assistant",
          content: "You need to sign in to use this bot. Redirecting to the sign-in page…",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        setTimeout(() => {
          window.location.href = `/login?redirect=${encodeURIComponent(`/chat/${apiKey}`)}`;
        }, 1200);
        return;
      }

      // Monthly quota hit — show the friendly limit message instead of a
      // generic error. The server has already refused before any AI call.
      if (res.status === 429) {
        let body: any = null;
        try { body = await res.clone().json(); } catch { /* non-JSON body */ }
        if (body?.code === "MESSAGE_LIMIT_REACHED") {
          const limitMsg: Message = {
            id: "m-" + Date.now() + "-limit",
            role: "assistant",
            content: `${body.title ?? "Monthly Message Limit Reached"}\n\n${body.message ?? "Your organization has reached the monthly message limit for the current plan. Please upgrade your plan or contact your administrator to continue using the chatbot."}`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, limitMsg]);
          return;
        }
      }

      // The backend requires a consented lead for this session (public bot,
      // and the caller is not same-org staff). Drop the just-sent message and
      // surface the pre-chat contact form so the visitor can introduce
      // themselves, then retry. This covers the cross-org signed-in case where
      // the client UI treated the user as staff but the server did not.
      if (res.status === 403) {
        let code = "";
        try { code = (await res.clone().json())?.code ?? ""; } catch { /* non-JSON body */ }
        if (code === "PRECHAT_REQUIRED") {
          setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
          setInput(messageText);
          setForcePreChat(true);
          return;
        }
      }

      const data = await res.json();

      const botMsg: Message = {
        id: "m-" + Date.now() + "-bot",
        role: "assistant",
        content: data.reply || "Sorry, I couldn't generate a response.",
        citations: data.citations,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMsg]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === targetConvoId
            ? {
                ...c,
                messages: [...c.messages, botMsg],
                backendConversationId: data.conversation_id || c.backendConversationId,
              }
            : c
        )
      );
    } catch {
      const errorMsg: Message = {
        id: "m-" + Date.now() + "-err",
        role: "assistant",
        content: "I'm having trouble connecting right now. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Live chat handoff ───────────────────────────────────────────
  // Convert the current AI conversation into a live human-agent
  // session. The visitor stays on this same page — we just route
  // composer + transcript through the live-chat endpoints instead of
  // /api/v1/chat. We use the active conversation's session id as the
  // handoff key so the operator can see the full bot transcript that
  // preceded the request.
  const requestHandoff = useCallback(async (departmentId?: string | null) => {
    if (!apiKey || handoffPending || recoveringLive || live) return;
    const generation = recoveryGeneration.current;
    const identity = tokenIdentity(getAuthToken());
    const current = () => generation === recoveryGeneration.current && identity === tokenIdentity(getAuthToken());
    setShowDeptPicker(false);
    setHandoffPending(true);
    // The server recovers existing sessions first, then decides whether a
    // new request can queue or needs explicit offline-message capture.
    // We need an active conversation to hand off — if the user clicks
    // before sending anything, spin up an empty conversation first so
    // we have a stable session id to bind to.
    let convo = activeConvo;
    let sessionId = convo?.sessionId;
    if (!convo || !sessionId) {
      const newConvoId = "c-" + Date.now();
      sessionId = generateSessionId();
      const newConvo: Conversation = {
        id: newConvoId, title: "Live agent", messages: [],
        sessionId, backendConversationId: null, createdAt: new Date(),
      };
      setConversations((prev) => [newConvo, ...prev]);
      setActiveConvoId(newConvoId);
      convo = newConvo;
    }
    try {
      if (!recoveryScopeRef.current) throw new Error("Session storage is unavailable");
      // Store the exact request BEFORE sending so a lost response/reload can
      // only retry the same durable escalation identity.
      const record: LiveRecovery = readLiveRecovery(sessionStorage, recoveryScopeRef.current) ?? {
        scope: recoveryScopeRef.current, retryScope: sessionId, requestKey: handoffRequestKey(sessionId),
        request: {
          apiKey, sessionId, conversationId: convo?.backendConversationId,
          visitorName: authedUser?.name ?? contact?.name ?? "",
          visitorEmail: authedUser?.email ?? contact?.email ?? "",
          departmentId: departmentId ?? null, reason: "User clicked Talk to a person",
        },
      };
      writeLiveRecovery(sessionStorage, record);
      const res = await fetch("/api/live-chat/handoff", {
        method: "POST",
        headers: { ...visitorHeaders(), "Idempotency-Key": record.requestKey },
        credentials: "omit",
        body: JSON.stringify({ ...record.request, recoverOnly: !!sessionStorage.getItem(offlineStorageKey()) }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => null);
        if (!current()) return;
        if (error?.code === "LIVE_CHAT_UNAVAILABLE") { showOfflineCapture(error.availability); return; }
        if (error?.code === "PRECHAT_REQUIRED") { await requireUpdatedPreChat(error.message); return; }
        throw new Error("Handoff failed");
      }
      const sess = await res.json();
      if (!current()) return;
      writeLiveRecovery(sessionStorage, { ...record, sessionId: sess.id, visitorToken: sess.visitorToken });
      setLive({
        sessionId: sess.id,
        retryScope: record.retryScope,
        visitorToken: sess.visitorToken ?? "",
        status: sess.status,
        position: null,
        operatorName: null,
        knownMessageIds: new Set(),
      });
      const sysMsg: Message = {
        id: "live-start-" + Date.now(),
        role: "system",
        content: "Connecting you to a live agent…",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, sysMsg]);
    } catch {
      if (!current()) return;
      const errorMsg: Message = {
        id: "live-err-" + Date.now(),
        role: "assistant",
        content: "Couldn't reach a live agent right now. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setHandoffPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, activeConvo, authedUser, contact, live, handoffPending, recoveringLive]);
  useEffect(() => {
    if (!preChatDone || !resumeHandoffAfterPrechat.current || live || handoffPending || recoveringLive) return;
    const intent = resumeHandoffAfterPrechat.current;
    resumeHandoffAfterPrechat.current = null;
    void requestHandoff(intent.departmentId);
  }, [preChatDone, live, handoffPending, recoveringLive, requestHandoff]);

  // Entry point for the "Talk to a person" buttons. If departments are
  // available (signed-in users), open the picker so the visitor can route
  // to the right team; otherwise hand off straight to the general queue.
  const onTalkToPerson = useCallback(() => {
    if (handoffPending || live) return;
    // Department list not back yet — defer so an authed user doesn't skip
    // the picker. The effect below resumes once the fetch settles.
    if (!deptsLoaded) { setPendingTalk(true); return; }
    if (departments.length > 0) { setShowDeptPicker(true); return; }
    requestHandoff(null);
  }, [deptsLoaded, departments.length, handoffPending, live, requestHandoff]);

  // Resume a deferred "Talk to a person" click once departments resolve.
  useEffect(() => {
    if (!pendingTalk || !deptsLoaded) return;
    setPendingTalk(false);
    if (live || handoffPending) return;
    if (departments.length > 0) setShowDeptPicker(true);
    else requestHandoff(null);
  }, [pendingTalk, deptsLoaded, departments.length, live, handoffPending, requestHandoff]);

  // Submit the offline form: posts the visitor's contact details to
  // the public offline-message endpoint and locks the form into a
  // "submitted" state so they get acknowledgement.
  const submitOfflineForm = useCallback(async () => {
    if (!apiKey || !offlineForm) return;
    if (!offlineForm.name.trim() || !offlineForm.email.trim() || !offlineForm.message.trim()) return;
    if (offlineBusy || !offlineForm.consent) return;
    const generation = recoveryGeneration.current;
    const identity = tokenIdentity(getAuthToken());
    const current = () => generation === recoveryGeneration.current && identity === tokenIdentity(getAuthToken());
    const storageKey = offlineStorageKey();
    setOfflineBusy(true); setOfflineError("");
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(offlineForm));
      const res = await fetch("/api/live-chat/offline-messages/public", {
        method: "POST",
        credentials: "omit",
        headers: visitorHeaders(),
        body: JSON.stringify({
          apiKey,
          sessionId: (recoveryScopeRef.current && readLiveRecovery(sessionStorage, recoveryScopeRef.current)?.request.sessionId)
            || activeConvo?.sessionId || gateSessionId,
          countryCode: contact?.countryCode ?? pcCountry,
          phone: contact?.phone ?? pcPhone,
          name: offlineForm.name.trim(),
          email: offlineForm.email.trim(),
          message: offlineForm.message.trim(),
          submissionKey: offlineForm.submissionKey,
          consent: offlineForm.consent,
        }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => null);
        throw new Error(error?.message || "Your message could not be saved. Please retry with the same details.");
      }
      if (!current()) return;
      setOfflineForm({ ...offlineForm, submitted: true });
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(LIVE_RECOVERY_KEY);
    } catch (error) { if (current()) setOfflineError(error instanceof Error ? error.message : "Your message could not be saved. Please retry."); }
    finally { if (current()) setOfflineBusy(false); }
  }, [apiKey, offlineForm, offlineBusy, activeConvo, contact, gateSessionId, pcCountry, pcPhone]);

  // Poll the live session state. Cadence is 2s — fast enough to feel
  // like real time, slow enough not to hammer the API. Stops once the
  // session closes.
  useEffect(() => {
    if (!live || live.status === "closed") return;
    const generation = recoveryGeneration.current;
    const identity = tokenIdentity(getAuthToken());
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`/api/live-chat/visitor/${live.sessionId}/state`, { credentials: "omit", headers: { "X-Visitor-Token": live.visitorToken, "X-Chatbot-Key": apiKey! } });
        if (!res.ok) return;
        const state = await res.json() as {
          status: "queued" | "active" | "escalated" | "closed";
          position: number | null;
          operatorName: string | null;
          messages: Array<{
            id: string; sender: string; content: string;
            attachmentUrl?: string | null; attachmentName?: string | null; attachmentMime?: string | null;
            createdAt: string;
          }>;
        };
        if (cancelled || generation !== recoveryGeneration.current || identity !== tokenIdentity(getAuthToken())) return;
        setLive((prev) => {
          if (!prev || prev.sessionId !== live.sessionId) return prev;
          // Append only messages we haven't already shown. Visitor's
          // own messages are already in `messages` (we add them
          // optimistically when the user sends), so we filter
          // sender==="visitor" out of the merge.
          const fresh = state.messages.filter(m => !prev.knownMessageIds.has(m.id) && m.sender !== "visitor");
          if (fresh.length > 0) {
            const newMsgs: Message[] = fresh.map(m => ({
              id: m.id,
              role: m.sender === "operator" ? "agent" : "system",
              content: m.content,
              timestamp: new Date(m.createdAt),
              attachmentUrl: m.attachmentUrl ?? undefined,
              attachmentName: m.attachmentName ?? undefined,
              attachmentMime: m.attachmentMime ?? undefined,
            }));
            setMessages((prevMsgs) => [...prevMsgs, ...newMsgs]);
          }
          // Surface a one-time "Operator joined" if status flipped.
          if (prev.status !== "active" && state.status === "active" && state.operatorName) {
            const joinMsg: Message = {
              id: "live-join-" + Date.now(),
              role: "system",
              content: `${state.operatorName} joined the chat.`,
              timestamp: new Date(),
            };
            setMessages((prevMsgs) => [...prevMsgs, joinMsg]);
          }
          if (prev.status !== "closed" && state.status === "closed") {
            const endMsg: Message = {
              id: "live-end-" + Date.now(),
              role: "system",
              content: "Human chat ended. You can continue with the AI assistant when ready.",
              timestamp: new Date(),
            };
            setMessages((prevMsgs) => [...prevMsgs, endMsg]);
          }
          const nextKnown = new Set(prev.knownMessageIds);
          state.messages.forEach(m => nextKnown.add(m.id));
          return {
            ...prev,
            status: state.status,
            position: state.position,
            operatorName: state.operatorName ?? prev.operatorName,
            knownMessageIds: nextKnown,
          };
        });
      } catch {/* ignore polling failures */}
    };
    tick();
    const i = window.setInterval(tick, 2000);
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    return () => {
      cancelled = true;
      window.clearInterval(i);
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
    };
  }, [live?.sessionId, live?.status]);

  // No-agent fallback: if the visitor sits in the queue for 2 minutes
  // without an operator claiming the session, tell them their details were
  // captured and someone will follow up, then drop back to the AI bot. The
  // queued session stays in the platform queue so an agent can still pick it
  // up offline.
  useEffect(() => {
    if (!live || live.status !== "queued") return;
    const queuedId = live.sessionId;
    const t = window.setTimeout(() => {
      // Re-validate against the latest committed state: an operator may have
      // claimed the session (status flipped to active) in the same tick the
      // timer fired, or a new session may have replaced this one.
      const cur = liveRef.current;
      if (!cur || cur.sessionId !== queuedId || cur.status !== "queued") return;
      setMessages((prev) => [
        ...prev,
        {
          id: "live-noagent-" + Date.now(),
          role: "assistant",
          content:
            "Your request is still in the live-support queue. An agent can join this conversation when available.",
          timestamp: new Date(),
        },
      ]);
      // Keep the durable session recoverable; it may still be claimed later.
    }, 120000);
    return () => window.clearTimeout(t);
  }, [live?.sessionId, live?.status]);

  // Keep closed human history until the visitor explicitly continues with AI.
  useEffect(() => {
    if (live?.status === "closed") {
      if (live.retryScope) completeHandoffRequest(live.retryScope);
      // Retain the closed capability for reload/history. New conversation
      // explicitly clears it; closure must never create a new escalation.
    }
  }, [live?.status]);

  const sendLiveMessage = useCallback(async (text: string) => {
    if (!live) return;
    const generation = recoveryGeneration.current;
    const optimistic: Message = {
      id: "v-" + Date.now(),
      role: "user",
      content: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    try {
      const res = await fetch(`/api/live-chat/visitor/${live.sessionId}/messages`, {
        credentials: "omit",
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Chatbot-Key": apiKey! },
        body: JSON.stringify({ apiKey, visitorToken: live.visitorToken, content: text }),
      });
      if (!res.ok) throw new Error("Message not accepted");
    } catch {
      if (generation !== recoveryGeneration.current) return;
      const err: Message = {
        id: "v-err-" + Date.now(),
        role: "system",
        content: "Couldn't deliver your message. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, err]);
    }
  }, [live]);

  const sendLiveFile = useCallback(async (file: File) => {
    if (!live) return;
    const generation = recoveryGeneration.current;
    const identity = tokenIdentity(getAuthToken());
    if (file.size > 5 * 1024 * 1024) {
      const err: Message = { id: "f-err-" + Date.now(), role: "system", content: "File exceeds 5 MB limit.", timestamp: new Date() };
      setMessages((prev) => [...prev, err]);
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    if (generation !== recoveryGeneration.current || identity !== tokenIdentity(getAuthToken())) return;
    const optimistic: Message = {
      id: "vf-" + Date.now(),
      role: "user",
      content: "",
      timestamp: new Date(),
      attachmentUrl: dataUrl,
      attachmentName: file.name,
      attachmentMime: file.type,
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const res = await fetch(`/api/live-chat/visitor/${live.sessionId}/upload`, {
        credentials: "omit",
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Chatbot-Key": apiKey! },
        body: JSON.stringify({ apiKey, visitorToken: live.visitorToken, dataUrl, fileName: file.name, mime: file.type }),
      });
      if (!res.ok) throw new Error("Upload not accepted");
    } catch {
      if (generation !== recoveryGeneration.current) return;
      const err: Message = {
        id: "f-err-" + Date.now(),
        role: "system",
        content: "Couldn't upload the file. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, err]);
    }
  }, [live]);

  const endLiveChat = useCallback(async () => {
    if (!live) return;
    try {
      const response = await fetch(`/api/live-chat/visitor/${live.sessionId}/close`, {
        credentials: "omit",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey, visitorToken: live.visitorToken }),
      });
      if (!response.ok) throw new Error("Close rejected");
      window.dispatchEvent(new Event("focus"));
    } catch {
      setMessages(previous => [...previous, { id: "close-error-" + Date.now(), role: "system",
        content: "Could not end the human chat. Please try End live chat again.", timestamp: new Date() }]);
    }
  }, [live]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Don't send while the mic is open — the user is still dictating.
      if (isRecording) return;
      const text = input.trim();
      if (!text) return;
      if (live && live.status !== "closed") {
        sendLiveMessage(text);
      } else {
        sendMessage();
      }
    }
  };

  const selectConversation = useCallback(async (convo: Conversation) => {
    setActiveConvoId(convo.id);
    setMessages(convo.messages);
    // Opening a conversation is never a "new reply": follow-latest resets ON
    // and the transcript scrolls to its latest message without the chip.
    followLatestRef.current = true;
    setNewReplyPending(false);
    // Close the narrow-layout drawer and hand focus to the composer (never
    // into the loaded history) so the user can reply immediately.
    setSidebarOpen((open) => (isNarrow ? false : open));
    // Focus after selecting a conversation is accessibility-specific:
    // only when the org ADA/WCAG toggle is ON.
    if (adaWcagRef.current) focusComposer();
    // Server-backed thread we haven't opened yet: lazy-load its transcript so
    // continuing the chat carries the full context.
    if (serverMode && convo.backendConversationId && convo.messages.length === 0) {
      const token = jwtToken();
      try {
        const res = await fetch(`/api/v1/my/conversations/${convo.backendConversationId}/messages`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const rows = (await res.json()) as Array<{
          id: string; role: string; content: string; createdAt: string; citations?: KnowledgeCitation[];
        }>;
        const msgs: Message[] = rows.map((m) => ({
          id: m.id,
          role: (m.role as Message["role"]) || "assistant",
          content: m.content,
          citations: m.citations,
          timestamp: new Date(m.createdAt),
        }));
        setMessages(msgs);
        setConversations((prev) => prev.map((c) => (c.id === convo.id ? { ...c, messages: msgs } : c)));
      } catch {
        // Leave whatever we have; reopening retries the load.
      }
    }
  }, [serverMode, isNarrow, focusComposer]);

  const deleteConversation = useCallback(async (id: string) => {
    // Capture the archived conversation object before clearing local state so
    // Undo can restore it verbatim.
    const convo = conversations.find((c) => c.id === id);
    // Server-backed thread: archive it on the server first (the DELETE route
    // now soft-archives, retaining history). Only drop it from local state
    // when the server confirms success — fetch() does not throw on 4xx/5xx.
    if (serverMode) {
      if (convo?.backendConversationId) {
        const token = jwtToken();
        const result = await archiveConversation(convo.backendConversationId, token, fetch);
        if (!result.success) {
          // Server rejected or network failed — do NOT remove from local state.
          setArchiveError(result.errorMessage ?? "Couldn't archive conversation. Please try again.");
          return;
        }
      }
    }
    setArchiveError(null);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (convo) setRecentlyArchived([convo]);
    setActiveConvoId((currentId) => {
      if (currentId === id) {
        setMessages([]);
        return null;
      }
      return currentId;
    });
  }, [serverMode, conversations]);

  // Restore the most recently archived conversation(s). For server-backed
  // threads this POSTs to /api/v1/my/conversations/{id}/restore for each
  // captured backend ID. Only re-inserts local objects when every restore
  // succeeds — fetch() does not throw on 4xx/5xx, so response.ok is checked.
  const restoreArchivedConversations = useCallback(async () => {
    const archived = recentlyArchived;
    if (archived.length === 0) return;
    if (serverMode) {
      const token = jwtToken();
      const backendIds = archived
        .map((c) => c.backendConversationId)
        .filter((x): x is string => !!x);
      if (backendIds.length > 0) {
        const result = await restoreConversations(backendIds, token, fetch);
        if (!result.success) {
          // Do NOT re-insert — keep the truthful local state (archived).
          setArchiveError(result.errorMessage ?? "Couldn't restore conversation(s). Please try again.");
          return;
        }
      }
    }
    // All restores succeeded (or no server IDs to restore) — re-insert.
    setArchiveError(null);
    setConversations((prev) => {
      const present = new Set(prev.map((c) => c.id));
      const missing = archived.filter((c) => !present.has(c.id));
      return [...missing, ...prev];
    });
    setRecentlyArchived([]);
  }, [recentlyArchived, serverMode]);

  // Staff users can pick a personal accent colour from the staff
  // portal's "Personalize" dialog. We use the hook (rather than reading
  // localStorage inline) so the chat re-renders when the user changes
  // their colour in another tab via the cross-tab `storage` event.
  //
  // We also intentionally do NOT gate on `fromPortal` here. The
  // localStorage key only ever gets set when a staff member uses the
  // portal's Personalize dialog, so external visitors / third-party
  // embeds never have it set and naturally fall back to the bot's
  // branded colour. Gating on `fromPortal` would silently drop the
  // override if a staff member bookmarks /chat/<key> directly, which
  // is exactly the case the user wants to avoid.
  const { primary: staffPrimary } = useStaffTheme();
  let hasStaffOverride = false;
  try {
    const v = typeof window !== "undefined" ? localStorage.getItem("staff-theme-primary") : null;
    hasStaffOverride = !!(v && /^#[0-9a-fA-F]{6}$/.test(v));
  } catch {
    hasStaffOverride = false;
  }
  const color = hasStaffOverride ? staffPrimary : (botConfig?.primaryColor || "#0c3547");
  // WCAG 1.4.3: never hardcode white on the admin-configured primary color —
  // pick whichever of white/near-black computes to the higher contrast
  // (always >= ~4.5:1 for any background).
  const onColorText = readableTextOn(color);

  if (configError) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 text-slate-900">
        <div className="text-center">
          <Bot className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h1 className="text-xl font-semibold mb-2">Bot Not Found</h1>
          <p className="text-slate-500">The chatbot you're looking for doesn't exist or is inactive.</p>
        </div>
      </div>
    );
  }

  if (!botConfig || !authChecked || authBotKey !== apiKey || recoveringLive) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // The full-screen surface (/chat/:apiKey) IS the staff experience: it opens
  // DIRECTLY with no pre-chat contact form. The contact form lives only on the
  // compact public widget (/widget/:apiKey). The single gate here: a bot that
  // REQUIRES sign-in sends anonymous visitors to login. (The backend re-checks
  // access on every call regardless.)
  if (!isWidget && !authedUser && botConfig.accessMode === "authenticated") {
    return <Redirect to={`/login?redirect=${encodeURIComponent(`/chat/${apiKey}`)}`} />;
  }

  // Pre-chat contact form gate — for every ANONYMOUS visitor of a bot whose
  // config requires the form (all public bots), on BOTH the compact widget
  // and the full-screen /chat surface, until completed for this session.
  // Signed-in staff skip it — unless the server explicitly demanded a lead
  // (forcePreChat, e.g. a user signed into a different org). Previously the
  // gate was widget-only, which dead-ended full-screen visitors and any
  // widget state where the server demanded a lead the client didn't expect
  // (defect D6 in the 2026-07-31 WCAG live audit).
  if (botConfig.preChatForm && !preChatDone && !live && (forcePreChat || !authedUser)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-900 px-4 py-10" data-testid="prechat-gate">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: color, color: onColorText }}>
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold leading-tight">{botConfig.name}</h1>
              <p className="text-xs text-slate-500">Before we start, please introduce yourself.</p>
            </div>
          </div>

          <form
            className="mt-5 space-y-4"
            noValidate
            onSubmit={(e) => { e.preventDefault(); submitPreChat(); }}
          >
            <div className="space-y-1.5" hidden={!pcField("name").visible}>
              <label htmlFor="pc-name" className="text-sm font-medium">{pcField("name").label}{pcField("name").required ? " *" : " (optional)"}</label>
              <input
                id="pc-name"
                type="text"
                value={pcName}
                onChange={(e) => setPcName(e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
                required={pcField("name").required}
                maxLength={pcField("name").maxLength}
                aria-invalid={pcErrorField === "name" || undefined}
                aria-describedby={pcErrorField === "name" ? "pc-error" : undefined}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ ["--tw-ring-color" as any]: color }}
                data-testid="input-prechat-name"
              />
            </div>

            <div className="space-y-1.5" hidden={!pcField("email").visible}>
              <label htmlFor="pc-email" className="text-sm font-medium">{pcField("email").label}{pcField("email").required ? " *" : " (optional)"}</label>
              <input
                id="pc-email"
                type="email"
                value={pcEmail}
                onChange={(e) => setPcEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required={pcField("email").required}
                maxLength={pcField("email").maxLength}
                aria-invalid={pcErrorField === "email" || undefined}
                aria-describedby={pcErrorField === "email" ? "pc-error" : undefined}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2"
                style={{ ["--tw-ring-color" as any]: color }}
                data-testid="input-prechat-email"
              />
            </div>

            <div className="space-y-1.5" hidden={!pcField("phone").visible && !pcField("countryCode").visible}>
              <label htmlFor="pc-phone" className="text-sm font-medium">{pcField("phone").visible ? pcField("phone").label : pcField("countryCode").label}{pcField("phone").required ? " *" : " (optional)"}</label>
              <div className="flex gap-2">
                <select
                  hidden={!pcField("countryCode").visible}
                  value={pcCountry}
                  onChange={(e) => setPcCountry(e.target.value)}
                  className="bg-white border border-slate-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 max-w-[8.5rem]"
                  style={{ ["--tw-ring-color" as any]: color }}
                  aria-label="Country dialling code"
                  data-testid="select-prechat-country"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.label} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <input
                  id="pc-phone"
                  hidden={!pcField("phone").visible}
                  type="tel"
                  value={pcPhone}
                  onChange={(e) => setPcPhone(e.target.value)}
                  placeholder="Phone number"
                  autoComplete="tel"
                  required={pcField("phone").required}
                  maxLength={pcField("phone").maxLength}
                  aria-invalid={pcErrorField === "phone" || undefined}
                  aria-describedby={pcErrorField === "phone" ? "pc-error" : undefined}
                  className="flex-1 min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2"
                  style={{ ["--tw-ring-color" as any]: color }}
                  data-testid="input-prechat-phone"
                />
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
              <input
                id="pc-consent"
                type="checkbox"
                checked={pcConsent}
                onChange={(e) => setPcConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
                aria-invalid={pcErrorField === "consent" || undefined}
                aria-describedby={pcErrorField === "consent" ? "pc-error" : undefined}
                data-testid="checkbox-prechat-consent"
              />
              <span>
                {pcField("consent").label}
                {" "}This information is kept confidential and is not shared with third parties or
                used for purposes unrelated to this conversation. Education records are handled in accordance with FERPA.
              </span>
            </label>

            {pcError && (
              <p id="pc-error" role="alert" className="text-xs text-red-600" data-testid="text-prechat-error">{pcError}</p>
            )}

            <button
              type="submit"
              disabled={pcSubmitting}
              className="w-full text-sm font-medium rounded-lg px-4 py-2.5 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: color, color: onColorText }}
              data-testid="button-prechat-submit"
            >
              {pcSubmitting ? "Starting…" : "Start chat"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Demo polish: pull the bot's predefined-question chips into the empty
  // state so a fresh visitor can try the bot without thinking up a prompt.
  const sortedQuestions = (botConfig.predefinedQuestions || [])
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const starters = sortedQuestions.slice(0, 4);

  // Group questions by their (per-bot, admin-defined) category so the guided
  // menu can present "areas of inquiry" first, then the questions within.
  const questionsByCategory = sortedQuestions.reduce<Record<string, PredefinedQuestion[]>>((acc, q) => {
    const cat = (q.category || "General").trim() || "General";
    (acc[cat] ||= []).push(q);
    return acc;
  }, {});
  const questionCategories = Object.keys(questionsByCategory);

  const inputLen = input.length;
  const inputMax = 1500;

  return (
    <div className={isWidget
      ? "min-h-screen flex items-center justify-center bg-slate-100 p-3 text-slate-900"
      // Locked app-shell layout: the chat viewport is EXACTLY the browser
      // viewport (h-[100dvh] + overflow-hidden), so the document itself never
      // scrolls — at any zoom. Header and composer stay visible; the message
      // history (<main>) is the only vertical scroll container. WCAG 1.4.10
      // reflow is satisfied INSIDE the panel: content wraps and the history
      // scrolls; the page must not grow taller than the viewport.
      : "h-[100dvh] overflow-hidden flex bg-slate-50 text-slate-900"} data-testid="fullscreen-chat">
      {/* Skip link (WCAG 2.4.1): first focusable element, visible on focus. */}
      <a
        href="#chat-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-white focus:text-slate-900 focus:px-3 focus:py-2 focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-emerald-500"
        data-testid="link-skip-to-content"
      >
        Skip to chat content
      </a>
      {/* Screen-reader live region (WCAG 4.1.3). With the ADA/WCAG toggle ON
          it is always in the DOM (reply + typing narration). With the toggle
          OFF no announcer node exists in the DOM, except transiently while an
          essential error status (send failure, auth/quota error) is being
          announced — those stay programmatically determinable regardless. */}
      {shouldRenderAnnouncer(announceEnabled, srAnnouncement) && (
        <div className="sr-only" aria-live="polite" aria-atomic="true" role="status" data-testid="status-chat-announcer">
          {srAnnouncement}
        </div>
      )}
      {/* ── Sidebar (staff full-screen only) ─────────────────────── */}
      {/* Narrow layout backdrop: click closes the drawer. Sits BELOW the
          drawer (z-30 < z-40) and is removed entirely when closed, so it can
          never invisibly block the menu, composer, or conversation rows. */}
      {!isWidget && isNarrow && sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40"
          aria-hidden="true"
          onClick={() => { setSidebarOpen(false); menuBtnRef.current?.focus(); }}
          data-testid="overlay-sidebar"
        />
      )}
      {!isWidget && (
      <div
        ref={drawerRef}
        {...(isNarrow ? { role: "dialog", "aria-modal": true, "aria-label": "Conversations menu" } : {})}
        className={isNarrow
          // Overlay drawer at 300–400% zoom / small screens: slides over the
          // chat; `invisible` when closed removes it from the tab order and
          // hit-testing (no offscreen focus traps, no invisible overlay).
          ? `fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] transform transition-transform duration-300 border-r border-slate-200 bg-white ${sidebarOpen ? "visible translate-x-0 shadow-2xl" : "invisible -translate-x-full"}`
          : `${sidebarOpen ? "w-64" : "w-0"} flex-shrink-0 transition-all duration-300 overflow-hidden border-r border-slate-200 bg-white`}
      >
        <div className={`${isNarrow ? "w-full" : "w-64"} h-full flex flex-col overflow-y-auto`}>
          {/* Brand */}
          <div className="px-4 pt-4 pb-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: color, color: onColorText }}>
              <Bot className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold truncate">{botConfig.name}</span>
          </div>

          {/* Primary nav — only the actions this bot really supports. */}
          <div className="px-3 pt-1 space-y-0.5">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-sm font-medium" data-testid="nav-ai-chat">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              AI Chat
            </div>
            <button
              type="button"
              onClick={startNewConversation}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="nav-new-thread"
            >
              <Plus className="w-4 h-4 text-slate-400" />
              New thread
            </button>
            {!live && (
              <button
                type="button"
                onClick={onTalkToPerson}
                disabled={handoffPending || pendingTalk}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid="nav-talk-person"
              >
                <Headphones className="w-4 h-4 text-slate-400" />
                {handoffPending || pendingTalk ? "Connecting…" : "Talk to a person"}
              </button>
            )}
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-slate-600" data-testid="nav-history">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-slate-400" />
                History
              </div>
              <span className="text-[10px] text-slate-400 tabular-nums" data-testid="text-history-count">{conversations.length}</span>
            </div>
          </div>

          {/* New chat */}
          <div className="px-3 pt-3">
            <button
              onClick={startNewConversation}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-sm font-medium"
              data-testid="button-new-chat"
            >
              <Plus className="w-4 h-4" />
              New chat
            </button>
          </div>

          {/* Recent Chat — every thread is preserved across reloads
              and clicking one resumes the conversation in place. */}
          <div className="px-3 pt-4 pb-1 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Recent Chat</span>
            <div className="flex items-center gap-2">
              {recentlyArchived.length > 0 && (
                <button
                  type="button"
                  onClick={() => { void restoreArchivedConversations(); }}
                  className="text-[10px] text-slate-400 hover:text-emerald-500 transition-colors"
                  data-testid="button-undo-archive-conversation"
                  title="Undo — restore the conversation(s) you just archived"
                  aria-label="Undo — restore the conversation or conversations you just archived"
                >
                  Undo
                </button>
              )}
              {conversations.length > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Archive all saved conversations? They'll be removed from your list, but their history is retained.")) return;
                    // Capture the archived conversations before mutating local
                    // state so Undo can restore exactly this set.
                    const archived = conversations;
                    if (serverMode) {
                      const token = jwtToken();
                      const ids = archived
                        .map((c) => c.backendConversationId)
                        .filter((x): x is string => !!x);
                      if (ids.length > 0) {
                        // All-or-nothing: only clear local state when ALL
                        // archive requests succeed. fetch() does not throw on
                        // 4xx/5xx so we must check response.ok explicitly.
                        const bulkResult = await bulkArchiveConversations(ids, token, fetch);
                        if (!bulkResult.allSucceeded) {
                          setArchiveError(bulkResult.errorMessage ?? "Couldn't archive all conversations. Please try again.");
                          return;
                        }
                      }
                    }
                    setArchiveError(null);
                    setRecentlyArchived(archived);
                    setConversations([]);
                    setActiveConvoId(null);
                    setMessages([]);
                  }}
                  className="text-[10px] text-slate-400 hover:text-red-500 transition-colors"
                  data-testid="button-clear-all-history"
                  title="Clear all conversations"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {/* Inline error for archive/restore failures (task #79: never show
              false success; surface the backend error near the action). */}
          {archiveError && (
            <div
              role="alert"
              className="mx-3 mb-1 px-2 py-1.5 rounded text-[11px] text-red-700 bg-red-50 border border-red-200"
              data-testid="text-archive-error"
            >
              {archiveError}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
            {conversations.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No conversations yet</div>
            ) : (
              conversations.map((convo) => {
                const created = convo.createdAt instanceof Date ? convo.createdAt : new Date(convo.createdAt);
                const time = formatRelativeTime(created);
                return (
                  <div
                    key={convo.id}
                    role="button"
                    tabIndex={0}
                    aria-current={activeConvoId === convo.id ? "true" : undefined}
                    className={`group flex items-start gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                      activeConvoId === convo.id ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    onClick={() => selectConversation(convo)}
                    onKeyDown={(e) => {
                      // WCAG 2.1.1: Enter/Space must activate this row like a
                      // button — but only when the row itself is focused, so
                      // keyboard-activating the inner delete button never
                      // bubbles into a row selection.
                      if (e.currentTarget !== e.target) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectConversation(convo);
                      }
                    }}
                    data-testid={`convo-${convo.id}`}
                    title={`Open conversation · ${convo.messages.length} message${convo.messages.length === 1 ? "" : "s"}`}
                  >
                    <MessageSquare className="w-4 h-4 flex-shrink-0 text-slate-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-[13px] leading-tight">{convo.title}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{time}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (window.confirm("Archive this conversation? It will be removed from your list, but its history is retained.")) deleteConversation(convo.id); }}
                      className="a11y-tip a11y-tip-below a11y-tip-end opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 hover:text-red-500 transition-opacity"
                      data-testid={`delete-convo-${convo.id}`}
                      data-tip="Archive this conversation"
                      aria-label="Archive this conversation"
                    >
                      <Trash2 className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Settings & Help */}
          <div className="px-3 pt-2 pb-3 border-t border-slate-200 space-y-0.5">
            <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-slate-400 font-medium">Settings &amp; Help</div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600">
              <Settings className="w-4 h-4 text-slate-400" />
              Settings
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600">
              <HelpCircle className="w-4 h-4 text-slate-400" />
              Help
            </div>
            {/* Light/Dark toggle (visual only) */}
            <div className="mt-2 grid grid-cols-2 rounded-lg bg-slate-100 p-1 text-xs">
              <div className="flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-white shadow-sm font-medium">
                <Sun className="w-3.5 h-3.5" /> Light
              </div>
              <div className="flex items-center justify-center gap-1.5 py-1.5 text-slate-500">
                <Moon className="w-3.5 h-3.5" /> Dark
              </div>
            </div>
            {authedUser && (
              <div className="mt-3 flex items-center gap-2 px-2" data-testid="text-fullscreen-signed-in-as">
                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{authedUser.name || authedUser.email}</div>
                  <div className="text-[10px] text-slate-400 truncate">{authedUser.email}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ── Main column ─────────────────────────────────────────── */}
      <div className={isWidget
        ? "w-full max-w-md h-[85vh] max-h-[720px] flex flex-col min-w-0 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        : "flex-1 flex flex-col min-w-0 bg-slate-50"}>
        {/* Top bar */}
        {/* min-h + wrap (not fixed h-14): at 200–400% zoom the controls must
            be allowed to wrap onto extra rows instead of overflowing or
            squeezing the conversation area out of the viewport. */}
        {/* compact (narrow OR short viewport, i.e. real 300–400% zoom):
            single non-wrapping row, minimal padding, icon-only secondary
            actions — the header must never eat the history's height. */}
        <header className={compact
          ? "flex items-center gap-x-1 px-2 py-1 border-b border-slate-200 bg-white flex-shrink-0"
          : "min-h-14 flex flex-wrap items-center gap-y-1 px-5 py-1.5 border-b border-slate-200 bg-white flex-shrink-0"}>
          {isWidget ? (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: color, color: onColorText }}>
              <Bot className="w-4 h-4" />
            </div>
          ) : (
            <button ref={menuBtnRef} onClick={() => setSidebarOpen(!sidebarOpen)} className="a11y-tip a11y-tip-below p-1.5 rounded-md hover:bg-slate-100 mr-3 text-slate-600" data-testid="button-toggle-sidebar"
              data-tip={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"} aria-expanded={sidebarOpen}>
              {sidebarOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </button>
          )}
          {/* min-w keeps the title readable at 300–400% zoom (never "A..");
              the wrap-enabled header lets the action buttons drop to the
              next row instead of squeezing the title out. */}
          <h1 className={compact ? "text-sm font-semibold flex-1 min-w-0 truncate" : "text-sm font-semibold flex-1 min-w-[8rem] break-words"}>
            {isWidget ? botConfig.name : "AI Chat"}
            {live && live.status === "active" && live.operatorName && (
              <span className="ml-2 text-[11px] text-emerald-600 font-normal">· chatting with {live.operatorName}</span>
            )}
            {live && live.status === "queued" && (
              <span className="ml-2 text-[11px] text-amber-600 font-normal">
                · waiting for an agent{live.position ? ` (#${live.position} in line)` : "…"}
              </span>
            )}
          </h1>
          {!live && (
            <button
              onClick={onTalkToPerson}
              disabled={handoffPending || pendingTalk}
              className={`inline-flex items-center gap-1.5 rounded-md text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50 ${compact ? "a11y-tip a11y-tip-below p-1.5" : "px-3 py-1.5 mr-2"}`}
              data-testid="button-talk-to-person"
              data-tip={compact ? "Talk to a person" : undefined}
              title={compact ? undefined : "Connect to a live human agent"}
              aria-label="Talk to a person"
            >
              <Headphones className="w-3.5 h-3.5" aria-hidden="true" />
              {!compact && (handoffPending || pendingTalk ? "Connecting…" : "Talk to a person")}
            </button>
          )}
          {live && live.status !== "closed" && (
            <button
              onClick={endLiveChat}
              className={`inline-flex items-center gap-1.5 rounded-md text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors ${compact ? "a11y-tip a11y-tip-below p-1.5" : "px-3 py-1.5 mr-2"}`}
              data-testid="button-end-live"
              data-tip={compact ? "End live chat" : undefined}
              aria-label="End live chat"
              title={compact ? undefined : "End live chat"}
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
              {!compact && "End live chat"}
            </button>
          )}
          {live?.status === "closed" && (
            <button type="button" onClick={() => setLive(null)}
              className="px-3 py-1.5 text-xs rounded-md border" data-testid="button-continue-ai">
              Continue with AI
            </button>
          )}
          {!isWidget && authedUser && !compact && (
            <div className="hidden sm:flex items-center gap-2 mr-3 pl-3 border-l border-slate-200" data-testid="text-topbar-signed-in">
              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="min-w-0 leading-tight text-right">
                <div className="text-xs font-medium truncate" data-testid="text-topbar-user-name">{authedUser.name || authedUser.email}</div>
                <div className="text-[10px] text-slate-400 truncate" data-testid="text-topbar-user-org">
                  {authedUser.orgName ? `on ${authedUser.orgName}` : "on the platform"}
                </div>
              </div>
            </div>
          )}
          {!isWidget && (
            <button
              onClick={() => {
                if (authedUser) {
                  // Close this bot session and return to the platform. The app
                  // mounts the role-aware home (Dashboard / Staff portal / Live
                  // chat) at "/", so always route there.
                  try { sessionStorage.removeItem(`dotnetbot.prechat.${apiKey}`); } catch {/* ignore */}
                  setLocation("/");
                } else if (typeof window !== "undefined" && window.history.length > 1) {
                  window.history.back();
                } else {
                  setLocation(apiKey ? `/portal/${apiKey}` : "/portal");
                }
              }}
              className={`inline-flex items-center gap-1.5 rounded-md text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors ${compact ? "a11y-tip a11y-tip-below p-1.5" : "px-3 py-1.5"}`}
              data-testid="button-exit-chat"
              data-tip={compact ? (authedUser ? "Back to platform" : "Exit chat") : undefined}
              title={compact ? undefined : (authedUser ? "Close this bot session and return to the platform" : "Exit and return to the platform")}
              aria-label={authedUser ? "Back to platform" : "Exit chat"}
            >
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
              {!compact && (authedUser ? "Back to platform" : "Exit")}
            </button>
          )}
          {isWidget && (
            <>
              {/* Maximize: opens the same chatbot in a full-window tab.
                  Same-origin localStorage (dotnetbot.jwt + dotnetbot.chat.*)
                  is shared automatically — no token in the URL needed. */}
              <button
                onClick={() => window.open(`/chat/${apiKey}`, "_blank", "noopener,noreferrer")}
                className="a11y-tip a11y-tip-below a11y-tip-end ml-1 inline-flex items-center justify-center p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid="button-maximize-widget"
                data-tip="Open full window"
                aria-label="Open chat in full window"
              >
                <Maximize2 className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                onClick={() => {
                  try { sessionStorage.removeItem(`dotnetbot.prechat.${apiKey}`); } catch {/* ignore */}
                  if (typeof window !== "undefined" && window.history.length > 1) {
                    window.history.back();
                  } else {
                    setLocation(apiKey ? `/portal/${apiKey}` : "/portal");
                  }
                }}
                className="a11y-tip a11y-tip-below a11y-tip-end ml-1 inline-flex items-center justify-center p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid="button-close-widget"
                data-tip="Close chat and return"
                aria-label="Close chat and return"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </>
          )}
        </header>

        {/* Offline-form takeover */}
        {offlineForm && (
          <div className="px-4 py-3 border-b border-slate-200 bg-white" data-testid="card-offline-form">
            <p className="text-sm mb-2">{offlineForm.notice}</p>
            {offlineError && <p role="alert" className="text-sm text-red-700 mb-2">{offlineError}</p>}
            {!offlineForm.submitted ? (
              <fieldset disabled={offlineBusy} className="max-w-xl mx-auto">
                <div className="text-sm font-medium mb-1">Leave a message</div>
                <p className="text-xs text-slate-500 mb-3">Send your message for our team to follow up by email.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <input type="text" placeholder="Your name" aria-label="Your name" required value={offlineForm.name}
                    onChange={(e) => setOfflineForm({ ...offlineForm, name: e.target.value })}
                    className="bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-sm placeholder:text-slate-400"
                    data-testid="input-offline-name" />
                  <input type="email" placeholder="Email" aria-label="Email" required value={offlineForm.email}
                    onChange={(e) => setOfflineForm({ ...offlineForm, email: e.target.value })}
                    className="bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-sm placeholder:text-slate-400"
                    data-testid="input-offline-email" />
                </div>
                <textarea placeholder="How can we help?" aria-label="How can we help?" rows={3} value={offlineForm.message}
                  onChange={(e) => setOfflineForm({ ...offlineForm, message: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-sm placeholder:text-slate-400 mb-2"
                  data-testid="input-offline-message" />
                <div className="flex items-center justify-end gap-2">
                  <label className="text-xs"><input type="checkbox" checked={offlineForm.consent}
                    onChange={e => setOfflineForm({ ...offlineForm, consent: e.target.checked })} /> I consent to being contacted about this message.</label>
                  <button onClick={() => setOfflineForm(null)} className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1" data-testid="button-offline-cancel">Cancel</button>
                  <button onClick={submitOfflineForm}
                    disabled={offlineBusy || !offlineForm.consent || !offlineForm.name.trim() || !offlineForm.email.trim() || !offlineForm.message.trim()}
                    className="text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
                    style={{ backgroundColor: color, color: onColorText }} data-testid="button-offline-submit">Send message</button>
                </div>
              </fieldset>
            ) : (
              <div className="max-w-xl mx-auto text-center py-2" data-testid="text-offline-submitted">
                <div className="text-sm font-medium mb-1">Thanks — we got your message.</div>
                <p className="text-xs text-slate-500 mb-2">Your message is saved for our team to follow up. No response deadline is promised.</p>
                <button onClick={() => setOfflineForm(null)} className="text-xs text-slate-600 hover:text-slate-900 underline" data-testid="button-offline-dismiss">Back to the bot</button>
              </div>
            )}
          </div>
        )}

        {/* Messages / Welcome */}
        <main
          id="chat-main"
          aria-label="Chat conversation"
          ref={chatMainRef}
          onScroll={handleChatScroll}
          // min-h-0 (not a fixed min): inside the locked shell the history
          // must SHRINK and scroll internally rather than push the composer
          // below the viewport at high zoom.
          // compact: guarantee the history a usable minimum height (min-h-16)
          // so a tall header/composer can never collapse it to zero at 400%.
          className={`flex-1 overflow-y-auto [scroll-padding-block:1rem] ${compact ? "min-h-16" : "min-h-0"}`}
          tabIndex={-1}
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-4 py-10">
              {/* Glowing orb */}
              <div className={isWidget ? "relative mb-5" : "relative mb-8"}>
                <div className="absolute inset-0 rounded-full bg-emerald-300 blur-3xl opacity-60" />
                <div
                  className={`relative ${isWidget ? "w-16 h-16" : "w-28 h-28"} rounded-full shadow-xl`}
                  style={{
                    background:
                      "radial-gradient(circle at 35% 30%, #d9f99d 0%, #4ade80 40%, #16a34a 75%, #064e3b 100%)",
                  }}
                />
              </div>
              <h2 className={`${isWidget ? "text-xl" : "text-3xl"} font-semibold mb-2 text-center`}>Welcome to {botConfig.name}</h2>
              <p className="text-slate-500 text-center max-w-md">
                {botConfig.welcomeMessage || "Get started by giving me a task and Chat can do the rest."}
                <br />
                <span className="text-slate-400">Not sure where to start?</span>
              </p>

              {/* Guided menu (public compact widget only): pick an area of
                  inquiry, then a question. Categories come from the bot's
                  predefined questions, so each bot defines its own areas. The
                  staff full-screen surface stays a plain direct chat window. */}
              {isWidget && questionCategories.length > 0 && (
                <div className="mt-8 w-full max-w-md">
                  {(() => {
                    const multi = questionCategories.length > 1;
                    const active = menuCategory && questionsByCategory[menuCategory]
                      ? menuCategory
                      : (multi ? null : questionCategories[0]);
                    return (
                      <>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 justify-center">
                          <Sparkles className="w-3.5 h-3.5" style={{ color }} />
                          <span>{active ? "Pick a question to get started" : "Choose an area of inquiry"}</span>
                        </div>
                        {!active ? (
                          <div className="space-y-2" data-testid="panel-categories">
                            {questionCategories.map((cat) => (
                              <button
                                key={cat}
                                onClick={() => setMenuCategory(cat)}
                                className="w-full flex items-center justify-between gap-2 text-left text-sm px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
                                data-testid={`button-category-${cat}`}
                              >
                                <span className="font-medium">{cat}</span>
                                <span className="text-xs text-slate-400">{questionsByCategory[cat].length}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2" data-testid="panel-questions">
                            {multi && (
                              <button
                                onClick={() => setMenuCategory(null)}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors mb-1"
                                data-testid="button-category-back"
                              >
                                <ArrowLeft className="w-3.5 h-3.5" /> All areas
                              </button>
                            )}
                            <div className="space-y-2">
                              {questionsByCategory[active].map((q, i) => (
                                <button
                                  key={i}
                                  onClick={() => sendMessage(q.question)}
                                  className="w-full text-left text-sm px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
                                  data-testid={`button-starter-${i}`}
                                >
                                  {q.question}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

            </div>
          ) : (
            <div className="max-w-3xl mx-auto py-6 px-4 space-y-6">
              {messages.map((msg, i) => {
                if (msg.role === "system") {
                  return (
                    <div key={msg.id} className="text-center text-[11px] text-slate-400 italic" data-testid={`msg-system-${msg.id}`}>
                      {msg.content}
                    </div>
                  );
                }
                const isAgent = msg.role === "agent";
                return (
                <div
                  key={msg.id}
                  // Latest-message anchors for adaptive auto-scroll; the
                  // callback re-runs each render so the refs always point at
                  // the newest user / bot rows.
                  ref={(el) => {
                    if (i === lastUserIdx) latestUserMsgRef.current = el;
                    if (i === lastBotIdx) latestBotMsgRef.current = el;
                  }}
                  // Keep the anchored message clear of the sticky header /
                  // composer when it is scrolled to (scroll-margin-block).
                  className={`flex gap-3 scroll-mt-4 scroll-mb-24 ${msg.role === "user" ? "justify-end" : ""}`}
                  data-testid={`msg-${msg.id}`}
                >
                  {msg.role !== "user" && (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1"
                         style={{ backgroundColor: isAgent ? "#16a34a" : color, color: readableTextOn(isAgent ? "#16a34a" : color) }}
                         title={isAgent ? "Live agent" : "AI assistant"}>
                      {isAgent ? <Headphones className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </div>
                  )}
                  <div className={`max-w-[80%] min-w-0 overflow-hidden ${msg.role === "user" ? "rounded-2xl rounded-tr-sm px-4 py-2.5" : "flex-1 bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2.5"}`}
                       style={msg.role === "user" ? { backgroundColor: color, color: onColorText } : undefined}>
                    {isAgent && (
                      <div className="text-[10px] uppercase tracking-wider text-emerald-600 mb-1">Live agent</div>
                    )}
                    {msg.role === "user" ? (
                      <>
                        {msg.content && (() => {
                          const meta = getMessageLangMeta(msg.content, selectedLanguage);
                          return <p lang={meta.lang} dir={meta.dir} className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>;
                        })()}
                        {msg.attachmentUrl && renderAttachment(msg)}
                      </>
                    ) : isAgent ? (
                      <>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        {msg.attachmentUrl && renderAttachment(msg)}
                      </>
                    ) : (
                      (() => {
                        // WCAG 3.1.2: tag each reply with its language and
                        // direction so screen readers pronounce it correctly
                        // and Urdu/Arabic render right-to-left.
                        const meta = getMessageLangMeta(msg.content, selectedLanguage);
                        return (
                          <div
                            lang={meta.lang}
                            dir={meta.dir}
                            className={`prose prose-sm max-w-none break-words [&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3 [&>h2]:text-base [&>h3]:text-sm [&>h2]:font-semibold [&>h3]:font-semibold [&>h2]:mb-2 [&>h3]:mb-2 [&_a]:break-words [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_code]:break-words [&_table]:block [&_table]:overflow-x-auto [&_img]:max-w-full [&_img]:h-auto${meta.dir === "rtl" ? " text-right" : ""}`}
                          >
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                            <KnowledgeCitations citations={msg.citations} />
                          </div>
                        );
                      })()
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-slate-200">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                </div>
                );
              })}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color, color: onColorText }}>
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1.5 py-3" aria-hidden="true">
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
              {/* Recurring guided menu (public compact widget only): after each
                  response let the visitor pick another question or step back to
                  the areas. Free typing still works via the composer below. */}
              {isWidget && !isLoading && !live && questionCategories.length > 0 && (
                <div className="pt-2" data-testid="panel-recurring-menu">
                  {(() => {
                    const multi = questionCategories.length > 1;
                    const active = menuCategory && questionsByCategory[menuCategory]
                      ? menuCategory
                      : (multi ? null : questionCategories[0]);
                    return (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2.5">
                          <Sparkles className="w-3.5 h-3.5" style={{ color }} />
                          <span>{active ? "Ask another question" : "Choose an area of inquiry"}</span>
                        </div>
                        {!active ? (
                          <div className="space-y-2">
                            {questionCategories.map((cat) => (
                              <button
                                key={cat}
                                onClick={() => setMenuCategory(cat)}
                                className="w-full flex items-center justify-between gap-2 text-left text-sm px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
                                data-testid={`button-recurring-category-${cat}`}
                              >
                                <span className="font-medium">{cat}</span>
                                <span className="text-xs text-slate-400">{questionsByCategory[cat].length}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {multi && (
                              <button
                                onClick={() => setMenuCategory(null)}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors mb-1"
                                data-testid="button-recurring-category-back"
                              >
                                <ArrowLeft className="w-3.5 h-3.5" /> All areas
                              </button>
                            )}
                            <div className="space-y-2">
                              {questionsByCategory[active].map((q, i) => (
                                <button
                                  key={i}
                                  onClick={() => sendMessage(q.question)}
                                  className="w-full text-left text-sm px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
                                  data-testid={`button-recurring-starter-${i}`}
                                >
                                  {q.question}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              <div ref={messagesEndRef} className="scroll-mb-4" />
            </div>
          )}
        </main>

        {/* Composer — compact mode keeps it to a small fraction of a 400%-zoom
            viewport: tight padding, short textarea cap, no disclaimer. */}
        <div className={compact ? "px-2 pt-1 pb-1.5 flex-shrink-0" : "px-4 pt-2 pb-4 flex-shrink-0"}>
          <div className="max-w-3xl mx-auto relative">
            {/* Floating (absolute) chip: adds ZERO layout height, so its
                appearance can never push the composer below a 400%-zoom
                viewport or shrink the history panel. */}
            {newReplyPending && (
              <div className="absolute -top-10 inset-x-0 z-10 flex justify-center pointer-events-none [&>button]:pointer-events-auto">
                <button
                  type="button"
                  onClick={() => scrollToLatest(latestBotMsgRef.current)}
                  className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs px-3 py-1.5 shadow-lg hover:bg-slate-700 transition-colors"
                  data-testid="button-new-reply"
                >
                  New reply <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            )}
            <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm focus-within:border-slate-300 transition-colors">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, inputMax))}
                onKeyDown={handleKeyDown}
                placeholder={live && live.status !== "closed" ? "Message the agent…" : "Ask me anything..."}
                aria-label={live && live.status !== "closed" ? "Message to the live agent" : "Message to the assistant"}
                rows={1}
                className={`w-full bg-transparent text-slate-900 placeholder-slate-600 resize-none outline-none text-sm ${compact ? "px-3 pt-2 pb-1 pr-11 max-h-16 overflow-y-auto" : "px-4 pt-3 pb-2 pr-12 max-h-[200px]"}`}
                // NOT disabled during isLoading: disabling blurs the input,
                // forcing a click before the next message. sendMessage/
                // handleKeyDown already guard against double-sends; the user
                // can keep typing while the assistant responds.
                disabled={isRecording || (live?.status === "closed")}
                data-testid="input-message"
              />
              <button
                onClick={() => {
                  const text = input.trim();
                  if (!text) return;
                  if (live && live.status !== "closed") sendLiveMessage(text);
                  else sendMessage();
                }}
                disabled={!input.trim() || isLoading || isRecording || (live?.status === "closed")}
                className="a11y-tip a11y-tip-end absolute right-2 top-2 p-2 rounded-lg transition-colors disabled:opacity-40"
                style={{ backgroundColor: live ? "#16a34a" : color, color: readableTextOn(live ? "#16a34a" : color) }}
                data-testid="button-send"
                data-tip="Send message"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" aria-hidden="true" />
              </button>
              <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-slate-500 ${compact ? "px-2 pb-1" : "px-3 pb-2"}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5" title={AUTO_LANGUAGE_HELP}>
                    <Languages className="w-3.5 h-3.5" aria-hidden="true" />
                    <select
                      value={selectedLanguage}
                      onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                        // Explicit choice persists across refreshes; returning to
                        // Auto REMOVES the stored override so nothing is forced.
                        try {
                          if (e.target.value) localStorage.setItem("dotnetbot.chatLanguage", e.target.value);
                          else localStorage.removeItem("dotnetbot.chatLanguage");
                        } catch {}
                      }}
                      className="bg-transparent text-xs text-slate-500 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400 rounded cursor-pointer"
                      aria-label="Choose the language the assistant replies in"
                      aria-describedby="chat-language-help"
                      data-testid="select-chat-language"
                    >
                      {CHAT_LANGUAGES.map((l) => (
                        <option key={l.value || "auto"} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                    <span id="chat-language-help" className="sr-only">{AUTO_LANGUAGE_HELP}</span>
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && live && live.status !== "closed") sendLiveFile(f);
                      e.target.value = "";
                    }}
                    data-testid="input-visitor-file"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors${compact ? " a11y-tip" : ""}`}
                    data-testid="button-visitor-attach"
                    data-tip={compact ? "Attach a file" : undefined}
                    title={compact ? undefined : (live ? "Attach a file" : "Attachments are only available during live chat")}
                    aria-label="Attach a file"
                  >
                    <Paperclip className="w-3.5 h-3.5" aria-hidden="true" /> {!compact && "Attach"}
                  </button>
                  <button
                    type="button"
                    onClick={toggleVoiceRecording}
                    disabled={!voiceSupported}
                    aria-pressed={isRecording}
                    aria-label={isRecording ? "Stop recording and insert transcript" : "Start voice recording"}
                    className={`inline-flex items-center gap-1.5 transition-colors ${
                      isRecording
                        ? "text-red-600 hover:text-red-700"
                        : voiceSupported
                        ? "hover:text-slate-900"
                        : "opacity-50 cursor-not-allowed"
                    }${compact && !isRecording ? " a11y-tip" : ""}`}
                    data-tip={compact && !isRecording ? "Start voice recording" : undefined}
                    title={compact && !isRecording ? undefined : (voiceSupported ? (isRecording ? "Tap to stop and insert transcript" : "Start voice input") : "Voice input not supported in this browser")}
                    data-testid="button-voice"
                  >
                    {isRecording ? (
                      <>
                        <span className="relative flex h-2 w-2" aria-hidden="true">
                          <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                        </span>
                        Recording… tap to stop
                      </>
                    ) : (
                      <>
                        <Mic className="w-3.5 h-3.5" aria-hidden="true" /> {!compact && "Voice Message"}
                      </>
                    )}
                  </button>
                </div>
                {!compact && <div className="tabular-nums" data-testid="text-input-counter">{inputLen}/{inputMax}</div>}
              </div>
            </div>
            {voiceError && (
              <p
                className="text-[11px] text-red-600 text-center mt-2"
                role="alert"
                data-testid="text-voice-error"
              >{voiceError}</p>
            )}
            {/* Recording state is operational status (WCAG 4.1.3) — always on. */}
            <span className="sr-only" role="status" aria-live="polite" data-testid="status-voice">
              {isRecording ? "Recording. Tap the voice button to stop and insert the transcript." : ""}
            </span>
            <p className={`text-[11px] text-slate-400 text-center mt-3 ${compact ? "hidden" : ""}`}>
              {live && live.status !== "closed"
                ? "You're now chatting with a live agent."
                : <>{botConfig.name} may display inaccurate info, so please double check the response. <span className="font-medium text-slate-500">Your Privacy &amp; {botConfig.name}.</span></>}
            </p>
          </div>
        </div>
      </div>
      {showDeptPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="modal-dept-picker">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl p-5">
            <h3 className="text-base font-semibold text-slate-900">Which team can help?</h3>
            <p className="text-xs text-slate-500 mt-1">Choose a department to route your request, or continue to the general queue.</p>
            <div className="mt-4 space-y-1.5 max-h-64 overflow-y-auto">
              {departments.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => requestHandoff(d.id)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-700 transition-colors"
                  data-testid={`button-dept-${d.id}`}
                >
                  {d.name}
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowDeptPicker(false)}
                className="px-3 py-1.5 rounded-md text-xs text-slate-500 hover:bg-slate-100 transition-colors"
                data-testid="button-dept-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => requestHandoff(null)}
                className="px-3 py-1.5 rounded-md text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                data-testid="button-dept-skip"
              >
                General queue →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
